import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { db } from '../firebase';
import Header from '../components/Header';
import api, { API_BASE } from '../api';
import {
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  IconButton as MuiIconButton,
} from '@mui/material';
import { HomeIcon, SettingsIcon, DeleteIcon, EditIcon, ArrowBackIcon, PlayArrowIcon, AddIcon, CameraAltIcon, RemoveIcon, CloseIcon, RefreshIcon } from '../components/MuiIcons';
import EmojiEventsIcon from '../components/EmojiEventsIcon';
import ListItemButton from '@mui/material/ListItemButton';
import EntryImage from '../components/EntryImage';
import { getUserByEmail } from '../utils/userUtils';
import Webcam from 'react-webcam';
import { BrowserMultiFormatReader } from '@zxing/library';

const drawerWidth = 220;

type Entry = {
  id: string;
  name: string;
  description?: string;
  submittedBy?: string;
  submittedByUserId?: string;
  enteredByEmail?: string | null;
  participantEmail?: string;
  submittedAt: any;
  randomNumber?: number;
  publishedAt?: any;
  year?: number | null;
  winery?: string;
  variety?: string;
  region?: string;
  alcohol?: string;
  imageUrl?: string;
  imagePath?: string;
};

const CompetitionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation<{ name?: string; type?: string }>();

  // Parse user once and store in state
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const entriesRef = useRef<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [participantNames, setParticipantNames] = useState<{ [email: string]: string }>({});
  const [submittedUserEmails, setSubmittedUserEmails] = useState<{ [id: string]: string }>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEntryName, setNewEntryName] = useState('');
  const [newEntryDescription, setNewEntryDescription] = useState('');
  const [newEntryYear, setNewEntryYear] = useState<number | null>(null);
  const [newEntryWinery, setNewEntryWinery] = useState('');
  const [newEntryVariety, setNewEntryVariety] = useState('');
  const [newEntryRegion, setNewEntryRegion] = useState('');
  const [newEntryAlcohol, setNewEntryAlcohol] = useState('');
  const [newEntrySubmittedBy, setNewEntrySubmittedBy] = useState('');
  const [newEntryParticipantEmail, setNewEntryParticipantEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
    const [participantNameDisabled, setParticipantNameDisabled] = useState(false);
    const [participantEmailDisabled, setParticipantEmailDisabled] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(() => {
    try {
      const cached = localStorage.getItem('hostCompetitions');
      if (cached) {
        const arr = JSON.parse(cached as string);
        if (Array.isArray(arr) && arr.find((c: any) => String(c.id) === String(id))) {
          return true;
        }
      }
    } catch (e) {
      // ignore
    }
    return false;
  });
  const [hostUserId, setHostUserId] = useState<string | null>(null);
  const [hostChecked, setHostChecked] = useState(false);
  const [competitionStatus, setCompetitionStatus] = useState<string>('draft');
  const [myEntry, setMyEntry] = useState<Entry | null>(null);
  const [entryNotes, setEntryNotes] = useState<{ [key: string]: string }>({});
  const [votingNotes, setVotingNotes] = useState<string>('');
  const [maxVotesPerPerson, setMaxVotesPerPerson] = useState(3);
  const [votesByEntry, setVotesByEntry] = useState<{ [key: string]: number }>({});
  const [allVotes, setAllVotes] = useState<{ [key: string]: number }>({});
  const [hasVoted, setHasVoted] = useState(false);
  const [lastSavedVotes, setLastSavedVotes] = useState<{ [key: string]: number }>({});
  const [lastSavedVotingNotes, setLastSavedVotingNotes] = useState<string>('');
  const [votesChanged, setVotesChanged] = useState<boolean>(false);
  const [lastPublishedAt, setLastPublishedAt] = useState<any>(null);
  const [suppressUnpublished, setSuppressUnpublished] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success'|'error'|'info'|'warning'>('success');
  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };
  // Camera / scanner state
  const [cameraOpen, setCameraOpen] = useState(false);
  const webcamRef = useRef<any>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [tempImagePath, setTempImagePath] = useState<string | null>(null);
  const [imageCleared, setImageCleared] = useState<boolean>(false);

  // Dynamic year options from 1970 through the current year
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1970 + 1 }, (_, i) => currentYear - i);
  
  // Alcohol number helpers: allow increments of 0.1 and non-negative values
  const parseAlcohol = (v: string) => {
    if (v === null || v === undefined || v === '') return NaN;
    const n = Number(String(v).trim());
    return Number.isFinite(n) ? n : NaN;
  };

  const formatAlcohol = (n: number) => {
    if (!Number.isFinite(n)) return '';
    const fixed = Number(Math.round(n * 10) / 10).toFixed(1);
    return fixed.replace(/\.0$/, '');
  };

  const incrementAlcohol = () => {
    const cur = parseAlcohol(newEntryAlcohol);
    const next = Number.isNaN(cur) ? 0.1 : Math.round((cur + 0.1) * 10) / 10;
    setNewEntryAlcohol(formatAlcohol(next));
  };

  const decrementAlcohol = () => {
    const cur = parseAlcohol(newEntryAlcohol);
    const next = Number.isNaN(cur) ? 0 : Math.max(0, Math.round((cur - 0.1) * 10) / 10);
    setNewEntryAlcohol(formatAlcohol(next));
  };

  // Parsing helpers: extract year and varietal from product/title text
  const extractYearFromText = (txt: any) => {
    if (!txt) return null;
    try {
      const m = String(txt).match(/\b(19|20)\d{2}\b/);
      return m ? Number(m[0]) : null;
    } catch (e) {
      return null;
    }
  };

  const _varietals = ['sauvignon blanc','pinot noir','merlot','cabernet sauvignon','riesling','chardonnay','syrah','shiraz','zinfandel','malbec','gewurztraminer','grenache','barbera','tempranillo'];
  const extractVarietalFromText = (txt: any) => {
    if (!txt) return null;
    const lower = String(txt).toLowerCase();
    for (const v of _varietals) {
      if (lower.includes(v)) {
        return v.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
      }
    }
    return null;
  };

  const parseCandidate = (candidate: any) => {
    let prodName = '';
    if (typeof candidate === 'string') prodName = candidate;
    else if (candidate) prodName = candidate.product_name || candidate.name || candidate.title || (candidate.products && candidate.products[0] && candidate.products[0].name) || (candidate.classes && candidate.classes[0] && (candidate.classes[0].name || candidate.classes[0])) || '';
    prodName = String(prodName || '').trim();
    return {
      prodName,
      year: extractYearFromText(prodName),
      variety: extractVarietalFromText(prodName),
    };
  };
  
  const CONFIDENCE_THRESHOLD = 0.8;
  const normalizeConfidence = (v: number) => {
    if (!Number.isFinite(v)) return 0;
    if (v > 1) {
      if (v <= 100) return Math.min(1, v / 100);
      return Math.min(1, v / 1000);
    }
    return Math.max(0, Math.min(1, v));
  };

  const getCandidateConfidence = (c: any) => {
    if (!c) return 0;
    const raw = Number(c.score ?? c.confidence ?? c.confidence_score ?? c.confidenceScore ?? 0) || 0;
    return normalizeConfidence(raw);
  };

  const showLowConfidenceToast = () => {
    setToastMessage('No confident match found — try recapturing the wine label');
    setToastOpen(true);
  };
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  // Lookup / suggestion state
  const [lookupResults, setLookupResults] = useState<any[]>([]);
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false);
  const [selectedLookupIndex, setSelectedLookupIndex] = useState<number | null>(null);
  const [pendingAttach, setPendingAttach] = useState<any | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  // UI state for in-flight lookup and toast
  const [lookupInProgress, setLookupInProgress] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [forceOverwriteOnNextLookup, setForceOverwriteOnNextLookup] = useState(false);

  const competitionName = location.state?.name || 'Battle';
  const competitionType = (location.state?.type as 'Wine' | 'Food') || 'Wine';
    const isFood = String(location.state?.type || competitionType) === 'Food';

  // keep a ref in sync so polling closures can access latest entries
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // Debug on every render
  console.log('🎨 RENDER:', { 
    isHost, 
    hostUserId, 
    userId: user?.id, 
    competitionStatus,
    entriesCount: entries.length,
    timestamp: new Date().toLocaleTimeString() 
  });

  // Debug state changes
  useEffect(() => {
    console.log('🔍 State changed:', { isHost, hostUserId, userId: user?.id, competitionStatus });
  }, [isHost, hostUserId, user?.id, competitionStatus]);

  // Resolve possibly-relative image URLs returned from backend to an absolute URL
  const resolveImageUrl = (maybeUrl?: string | null): string | undefined => {
    if (!maybeUrl) return undefined;
    try {
      if (maybeUrl.startsWith('/')) {
        let origin = window.location.origin;
        if (typeof API_BASE === 'string' && API_BASE.startsWith('http')) {
          origin = new URL(API_BASE).origin;
        }
        return `${origin}${maybeUrl}`;
      }
      return maybeUrl || undefined;
    } catch (e) {
      return maybeUrl || undefined;
    }
  };

  // Inline SVG placeholder: square 400x400 with centered camera icon and prompt text
  const PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
      '<rect width="100%" height="100%" rx="3" fill="%23f5f5f5"/>' +
      '<g transform="translate(200,150)" text-anchor="middle">' +
        '<rect x="-72" y="-44" width="144" height="88" rx="12" fill="%23e6e6e6"/>' +
        '<rect x="-52" y="-64" width="36" height="20" rx="6" fill="%23e6e6e6"/>' +
        '<circle cx="0" cy="0" r="28" fill="%23cfcfcf"/>' +
        '<circle cx="0" cy="0" r="12" fill="%23ffffff"/>' +
      '</g>' +
      '<text x="200" y="320" font-family="Arial,Helvetica,sans-serif" font-size="18" fill="%23999" text-anchor="middle">Update to add image</text>' +
    '</svg>'
  )}`;

  function getEntryImageSrc(e: any): string | undefined {
    try {
      // If the user has cleared the image for the entry currently being edited,
      // prefer the placeholder rather than showing the old image.
      if (editingEntry && imageCleared && e && e.id && editingEntry.id === e.id) return undefined;
      if (e && e.imageUrl) return resolveImageUrl(e.imageUrl) || undefined;
      if (e && e.imagePath) {
        const p = String(e.imagePath || '').trim();
        if (p) return resolveImageUrl(api(`/uploads/${p}`)) || undefined;
      }
    } catch (err) {
      // fall through
    }
    return undefined;
  }

  // Placeholder moved to shared EntryImage component

  // Helper: title-case common wine metadata fields (keeps small words lowercase)
  const titleCase = (s?: string | null) => {
    if (!s) return '';
    return String(s).split(/\s+/).map((word, idx) => {
      // small words that often remain lowercase inside names
      const lower = word.toLowerCase();
      const small = ['and','or','the','a','an','of','in','on','de','du','la','le','von','van','y'];
      if (idx !== 0 && small.includes(lower)) return lower;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // Helper: normalize various timestamp shapes to milliseconds since epoch
  const getMillis = (ts: any): number | null => {
    if (ts === null || typeof ts === 'undefined') return null;
    if (typeof ts === 'number') return ts;
    if (ts instanceof Date) return ts.getTime();
    if (ts && typeof ts.toMillis === 'function') {
      try { return ts.toMillis(); } catch (e) { /* fallthrough */ }
    }
    if (ts && typeof ts._seconds === 'number') {
      return (Number(ts._seconds) * 1000) + Math.floor((Number(ts._nanoseconds) || 0) / 1e6);
    }
    const parsed = Date.parse(String(ts));
    if (!isNaN(parsed)) return parsed;
    return null;
  };

  const millisGT = (a: any, b: any) => {
    const aa = getMillis(a);
    const bb = getMillis(b);
    return aa !== null && bb !== null && aa > bb;
  };

  const millisLTE = (a: any, b: any) => {
    const aa = getMillis(a);
    const bb = getMillis(b);
    return aa !== null && bb !== null && aa <= bb;
  };

  // Preprocess an image dataURL for barcode decoding:
  // - center-crop to square, upscale, convert to grayscale, and auto-stretch contrast
  const preprocessForBarcode = async (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const w = img.width || 1;
          const h = img.height || 1;
          const size = Math.min(w, h) * 0.9; // crop a bit inside edges
          const sx = Math.max(0, Math.floor((w - size) / 2));
          const sy = Math.max(0, Math.floor((h - size) / 2));
          // Target size: upscale small images to aid decoding
          const target = Math.max(800, Math.floor(size * 2));
          const canvas = document.createElement('canvas');
          canvas.width = target;
          canvas.height = target;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(dataUrl);
          ctx.drawImage(img, sx, sy, size, size, 0, 0, target, target);

          try {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const d = imageData.data;
            let min = 255, max = 0;
            // compute luminance and find min/max
            for (let i = 0; i < d.length; i += 4) {
              const r = d[i], g = d[i + 1], b = d[i + 2];
              const l = 0.299 * r + 0.587 * g + 0.114 * b;
              if (l < min) min = l;
              if (l > max) max = l;
            }
            const range = (max - min) || 1;
            // write stretched grayscale back
            for (let i = 0; i < d.length; i += 4) {
              const r = d[i], g = d[i + 1], b = d[i + 2];
              const l = 0.299 * r + 0.587 * g + 0.114 * b;
              const v = Math.max(0, Math.min(255, Math.round(((l - min) / range) * 255)));
              d[i] = d[i + 1] = d[i + 2] = v;
            }
            ctx.putImageData(imageData, 0, 0);
          } catch (e) {
            // getImageData may fail for cross-origin images — fallback to raw draw
          }

          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch (err) {
          console.warn('preprocessForBarcode failed', err);
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const sentenceCase = (val: string) => {
    if (!val || typeof val !== 'string') return val || '';
    const s = val.trim();
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const setNewEntryNameTitle = (val: string) => setNewEntryName(sentenceCase(val));
  const setNewEntryWineryTitle = (val: string) => setNewEntryWinery(sentenceCase(val));
  const setNewEntryVarietyTitle = (val: string) => setNewEntryVariety(sentenceCase(val));
  const setNewEntryRegionTitle = (val: string) => setNewEntryRegion(sentenceCase(val));

  const applyLookupCandidate = (candidate: any, overwrite = false) => {
    try {
      const prodName = candidate && (candidate.product_name || candidate.name || candidate.title || candidate.product_name_en || candidate.product_name_fr || candidate.name_en || candidate.name_fr) || null;
      if (prodName) {
        if (overwrite) setNewEntryNameTitle(prodName);
        else setNewEntryName(prev => prev || sentenceCase(prodName));
      }

      if (candidate) {
        if (candidate.brands) {
          if (overwrite) setNewEntryWineryTitle(candidate.brands || '');
          else setNewEntryWinery(prev => prev || sentenceCase(candidate.brands || ''));
        }
        if (candidate.categories) {
          if (overwrite) setNewEntryVarietyTitle(candidate.categories || '');
          else setNewEntryVariety(prev => prev || sentenceCase(candidate.categories || ''));
        }
        if (candidate.countries) {
          setNewEntryRegion(prev => prev || sentenceCase(candidate.countries || ''));
        }
        if (candidate.nutriments && (candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g)) {
          setNewEntryAlcohol(prev => prev || String(candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g));
        } else if (candidate.alcohol) {
          setNewEntryAlcohol(prev => prev || String(candidate.alcohol));
        }

        try {
          const parsed = parseCandidate(candidate);
          if (parsed && parsed.year) {
            if (overwrite) setNewEntryYear(parsed.year);
            else setNewEntryYear(prev => prev || parsed.year);
          }
          if (parsed && parsed.variety) {
            if (overwrite) setNewEntryVarietyTitle(parsed.variety);
            else setNewEntryVariety(prev => prev || parsed.variety || '');
          }
        } catch (e) { /* ignore parseCandidate errors */ }
      }
    } catch (e) {
      console.warn('applyLookupCandidate failed', e);
    }
  };

  // Debounce function for saving notes
  const debounceTimeout = React.useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Calculate total votes used
  const totalVotesUsed = Object.values(votesByEntry).reduce((sum, count) => sum + count, 0);

  // Check if there are unpublished entries (for host only)
  const hasUnpublishedEntries = isHost && competitionStatus === 'started' && !suppressUnpublished && entries.some(entry => {
    if (!entry.publishedAt) return true;
    if (!lastPublishedAt) return false;
    // Check if entry was added/modified after last publish
    return millisGT((entry as any).publishedAt, lastPublishedAt);
  });

  // Filter entries based on user role
  const getVisibleEntries = () => {
    if (isHost) {
      // Host sees all entries
      return entries;
    }
    
    if (competitionStatus === 'draft') {
      // Participants in draft mode see their own entry by user id OR by their email (some entries use participantEmail)
      return entries.filter(e => e.submittedByUserId === user?.id || (e.participantEmail && user?.email && e.participantEmail === user.email));
    }
    
    // Participants in started mode only see published entries
    return entries.filter(entry => {
      if (!entry.name || !entry.randomNumber) return false;
      if (!entry.publishedAt) return false;
      if (!lastPublishedAt) return false;
      // Only show entries published before or at the last publish time
      return millisLTE(entry.publishedAt, lastPublishedAt);
    });
  };

  useEffect(() => {
    const loadCompetitionData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        // Determine whether the current user is a host via the backend to avoid client Firestore permissions
        let userIsHost = false;
        let finalHostUserId = user.id;
        try {
          const compsRes = await fetch(api(`/api/competitions/${user.id}`));
          if (compsRes.ok) {
            const compsBody = await compsRes.json();
            const comps: any[] = compsBody.competitions || [];
            const found = comps.find(c => String(c.id) === String(id));
            if (found) {
              userIsHost = !!found.isHost;
              finalHostUserId = found.ownerId || found.hostId || user.id;
            }
          } else {
            console.warn('Failed to fetch user battles for host determination', compsRes.status);
          }
        } catch (e) {
          console.warn('Error fetching user competitions for host determination', e);
        }

        setIsHost(userIsHost);
        setHostUserId(finalHostUserId);
        setHostChecked(true);

        // Fetch competition status and entries via backend API to avoid client-side Firestore permission issues
        try {
          const compRes = await fetch(api(`/api/competitions/${finalHostUserId}/${id}`));
          if (compRes.ok) {
            const compBody = await compRes.json();
            setCompetitionStatus(compBody.status || 'draft');
            setMaxVotesPerPerson(compBody.maxVotesPerPerson || 3);
            setLastPublishedAt(compBody.lastPublishedAt || null);
          } else {
            console.warn('Failed to load battle via backend', compRes.status);
          }
        } catch (e) {
          console.warn('Failed to load battle via backend', e);
        }

        try {
          const entriesRes = await fetch(api(`/api/competitions/${finalHostUserId}/${id}/entries`));
          if (entriesRes.ok) {
            const entriesBody = await entriesRes.json();
            const entriesData = (entriesBody.entries || []).map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              description: doc.description || '',
              submittedAt: doc.submittedAt,
              submittedBy: doc.submittedBy,
              submittedByUserId: doc.submittedByUserId,
              enteredByEmail: doc.enteredByEmail || null,
                participantEmail: doc.participantEmail,
                participantName: doc.participantName || doc.participantDisplayName || undefined,
              randomNumber: doc.randomNumber,
              publishedAt: doc.publishedAt,
              year: typeof doc.year !== 'undefined' ? doc.year : null,
              winery: doc.winery || '',
              variety: doc.variety || '',
              region: doc.region || '',
              alcohol: doc.alcohol || '',
              imageUrl: doc.imageUrl || '',
              imagePath: doc.imagePath || '',
            })) as Entry[];
            setEntries(entriesData);
            // Ensure participant's own entry is discovered even if email casing differs
            try {
              if (user?.email) {
                const normalizedEmail = user.email.trim().toLowerCase();
                const found = entriesData.find((e: Entry) => {
                  const pe = (e.participantEmail || '') as string;
                  return pe && pe.trim().toLowerCase() === normalizedEmail || (e.submittedByUserId && e.submittedByUserId === user.id);
                });
                if (found) setMyEntry(found);
              }
            } catch (e) {
              // non-fatal
            }
            setLoading(false);

            // Fetch display names for all unique participant emails
            const fetchNames = async () => {
              const uniqueEmails = Array.from(new Set(entriesData.map(e => e.participantEmail).filter((email): email is string => typeof email === 'string' && email.length > 0)));
              const namesMap: { [email: string]: string } = {};
              await Promise.all(uniqueEmails.map(async (email) => {
                if (!participantNames[email]) {
                  const userRecord = await getUserByEmail(email);
                  if (userRecord && (userRecord.displayName || userRecord.name)) {
                    namesMap[email] = userRecord.displayName || userRecord.name;
                  }
                } else {
                  namesMap[email] = participantNames[email]!;
                }
              }));
              if (Object.keys(namesMap).length > 0) {
                setParticipantNames((prev) => ({ ...prev, ...namesMap }));
              }
            };
            fetchNames();

            // Fetch submitter emails for entries that lack a valid enteredByEmail
            (async () => {
              try {
                const uniqueSubmitterIds = Array.from(new Set(entriesData.map((e: Entry) => e.submittedByUserId).filter((id): id is string => Boolean(id))));
                const toFetch = uniqueSubmitterIds.filter(id => id && !submittedUserEmails[id]);
                if (toFetch.length === 0) return;
                const map: { [id: string]: string } = {};
                await Promise.all(toFetch.map(async (id) => {
                  try {
                    const ures = await fetch(api(`/api/users/${id}`));
                    if (!ures.ok) return;
                    const ubody = await ures.json();
                    if (ubody && (ubody.email || ubody.displayName)) {
                      map[id] = ubody.email || ubody.displayName || '';
                    }
                  } catch (e) { /* ignore per-id failures */ }
                }));
                if (Object.keys(map).length > 0) setSubmittedUserEmails(prev => ({ ...prev, ...map }));
              } catch (e) {
                // non-fatal
              }
            })().catch(e => { console.warn('Background submitter-email fetch failed', e); });
          } else {
            console.warn('Failed to load entries via backend', entriesRes.status);
          }
        } catch (e) {
          console.warn('Failed to load entries via backend', e);
        }

        // Load participant's saved vote if competition is started (non-host users)
        if (!userIsHost) {
          // Use the dedicated participant endpoint to fetch the voter's own saved vote
          const voteRes = await fetch(api(`/api/competitions/${user.id}/${id}/votes/my`));
          if (voteRes.ok) {
            const voteData = await voteRes.json();
            console.log('Loaded participant vote data:', voteData);
            if (voteData.vote) {
              // Convert array of entryIds to vote counts
              const voteCounts: { [key: string]: number } = {};
              const entryIds = voteData.vote.favoriteEntryIds || voteData.vote.entryIds || [];
              entryIds.forEach((entryId: string) => {
                voteCounts[entryId] = (voteCounts[entryId] || 0) + 1;
              });
              setVotesByEntry(voteCounts);
              setVotingNotes(voteData.vote.notes || '');
              setHasVoted(true);
            }
          } else {
            console.log('No participant vote found or failed to load:', voteRes.status);
          }

          // Load user's notes
          const notesRes = await fetch(api(`/api/competitions/${user.id}/${id}/notes`));
          if (notesRes.ok) {
            const notesData = await notesRes.json();
            setEntryNotes(notesData.notes || {});
          }
        }

        // No real-time listeners used here; nothing to clean up.
      } catch (error) {
        console.error('Failed to load battle data:', error);
      } finally {
        // Mark that we've attempted to resolve host role so the UI can render.
        setHostChecked(true);
        setLoading(false);
      }
    };

    loadCompetitionData();
}, [id, user?.id]); // Only depend on id and user.id

  // Separate effect for vote polling (hosts) and entry polling (participants)
  useEffect(() => {
    console.log('🔄 Polling effect triggered!', { isHost, userId: user?.id, hostUserId });

    if (!user?.id || !hostUserId) {
      console.log('⏭️ Skipping polling - missing user or hostUserId:', { isHost, userId: user?.id, hostUserId });
      return;
    }

    // Function to refresh entries; shared for both host and participant polling
    const loadEntriesIfNeeded = async () => {
      try {
        // Don't stomp local edits or while saving.
        // Allow participants to still poll even if the entry dialog is open
        // (hosts may intentionally block polling while editing).
        if (saving) return;
        if (dialogOpen && isHost) return;
        const entriesRes = await fetch(api(`/api/competitions/${hostUserId}/${id}/entries`));
        if (entriesRes.ok) {
          const entriesBody = await entriesRes.json();
            const entriesData = (entriesBody.entries || []).map((doc: any) => ({
            id: doc.id,
            name: doc.name,
            description: doc.description || '',
            submittedAt: doc.submittedAt,
            submittedBy: doc.submittedBy,
              submittedByUserId: doc.submittedByUserId,
              enteredByEmail: doc.enteredByEmail || null,
            participantEmail: doc.participantEmail,
            participantName: doc.participantName || doc.participantDisplayName || undefined,
            randomNumber: doc.randomNumber,
            publishedAt: doc.publishedAt,
            year: typeof doc.year !== 'undefined' ? doc.year : null,
            winery: doc.winery || '',
            variety: doc.variety || '',
            region: doc.region || '',
            alcohol: doc.alcohol || '',
            imageUrl: doc.imageUrl || '',
            imagePath: doc.imagePath || '',
          })) as Entry[];

          // Update local entries state — compare against the current ref to avoid stale closure issues
          // Compare a lightweight digest that captures id + last-update timestamps and key fields
          const makeDigest = (list: Entry[]) => list.map(e => {
            const ts = (e as any).updatedAt ?? (e as any).publishedAt ?? (e as any).submittedAt ?? null;
            const tstr = ts ? (typeof ts === 'object' && ts.seconds ? String(ts.seconds) : String(ts)) : '';
            return `${e.id}|${e.name || ''}|${(e as any).participantEmail || ''}|${(e as any).participantName || e.submittedBy || ''}|${e.randomNumber || ''}|${tstr}`;
          }).join('::');

          const prevList = entriesRef.current || [];
          const prevDigest = makeDigest(prevList);
          const newDigest = makeDigest(entriesData);

          // Debug/logging to help diagnose staleness issues
          console.debug('Polling entries:', { prevCount: prevList.length, newCount: entriesData.length, prevDigest, newDigest });

          // If digest differs, apply new entries. Additionally, if any previously-known entry id is missing
          // from the new list, force update and clear dependent caches (myEntry / participantNames) so UI reflects deletions.
          const prevIds = new Set(prevList.map(p => p.id));
          const newIds = new Set(entriesData.map(p => p.id));
          let missingFromNew: string[] = [];
          for (const idd of prevIds) if (!newIds.has(idd)) missingFromNew.push(idd);

          if (prevDigest !== newDigest || missingFromNew.length > 0) {
            console.info('Applying polled entries update', { missingFromNew });
            setEntries(entriesData);

            // Recompute `myEntry` from the freshly-polled entries so participant UI
            // reflects host deletions even if the closure-captured `myEntry` was stale.
            try {
              if (user?.email || user?.id) {
                const normalizedEmail = user?.email ? user.email.trim().toLowerCase() : '';
                const found = entriesData.find((e: Entry) => {
                  const pe = (e.participantEmail || '') as string;
                  return (pe && normalizedEmail && pe.trim().toLowerCase() === normalizedEmail) || (e.submittedByUserId && e.submittedByUserId === user.id);
                });
                setMyEntry(found || null);
              }
            } catch (e) { /* ignore */ }

            // Clear participantNames entries that no longer exist to avoid stale display
            try {
              const emailsStillPresent = new Set(entriesData.map(e => (e.participantEmail || '').trim().toLowerCase()).filter(Boolean));
              setParticipantNames(prev => {
                const next: { [email: string]: string } = {};
                for (const [k, v] of Object.entries(prev || {})) {
                  if (emailsStillPresent.has(k.trim().toLowerCase())) next[k] = v;
                }
                return next;
              });
            } catch (e) { /* ignore */ }
          }
        }
      } catch (e) {
        console.warn('Failed to auto-refresh entries:', e);
      }
    };

    // Host: poll votes and entries
    if (isHost) {
      console.log('🎯 Starting vote polling for host');

      const loadVotes = async () => {
        try {
          console.log('🔄 Auto-loading votes at', new Date().toLocaleTimeString());
          const allVotesRes = await fetch(api(`/api/competitions/${hostUserId}/${id}/votes`));
          if (allVotesRes.ok) {
            const allVotesData = await allVotesRes.json();
            console.log('✅ Auto-loaded votes:', allVotesData.votes);
            setAllVotes(allVotesData.votes || {});
          } else {
            console.error('❌ Failed to auto-load votes:', allVotesRes.status);
          }
        } catch (error) {
          console.error('❌ Error auto-loading votes:', error);
        }
      };

      loadVotes(); // Load immediately
      loadEntriesIfNeeded();
      const votesInterval = setInterval(() => {
        loadVotes();
        loadEntriesIfNeeded();
      }, 3000); // Poll every 3 seconds

      return () => {
        console.log('🧹 Stopping vote polling');
        clearInterval(votesInterval);
      };
    }

    // Participant: poll entries so they see host deletions/edits
    console.log('🔎 Starting participant entry polling');
    // Also poll competition metadata (status/lastPublishedAt/maxVotesPerPerson)
    const loadCompetitionInfo = async () => {
      try {
        const compRes = await fetch(api(`/api/competitions/${hostUserId}/${id}`));
        if (compRes.ok) {
          const compBody = await compRes.json();
          if (compBody) {
            // Update status so participant UI reacts immediately when host stops voting
            if (compBody.status && compBody.status !== competitionStatus) setCompetitionStatus(compBody.status);
            if (typeof compBody.lastPublishedAt !== 'undefined') setLastPublishedAt(compBody.lastPublishedAt || null);
            if (typeof compBody.maxVotesPerPerson !== 'undefined') setMaxVotesPerPerson(compBody.maxVotesPerPerson || 3);
          }
        }
      } catch (e) {
        // non-fatal
      }
    };

    loadEntriesIfNeeded();
    loadCompetitionInfo();
    const partInterval = setInterval(() => {
      loadEntriesIfNeeded();
      loadCompetitionInfo();
    }, 3000);

    return () => {
      console.log('🧹 Stopping participant entry polling');
      clearInterval(partInterval);
    };
  }, [isHost, user?.id, hostUserId, id]);

  const handleMenuItemClick = (path: string) => {
    history.push(path);
    setDrawerOpen(false);
  };

  const handleOpenDialog = async (participantEmail?: string) => {
    // Always open in add mode
    setEditingEntry(null);
    setNewEntryName('');
    setNewEntryDescription('');
    setParticipantNameDisabled(false);
    setParticipantEmailDisabled(false);
    // Clear any previously-captured/temp image when starting a new entry
    setTempImageUrl(null);
    setTempImagePath(null);
    setCapturedImage(null);
    setImageCleared(false);
    // Clear wine fields for a new entry
    setNewEntryYear(null);
    setNewEntryWinery('');
    setNewEntryVariety('');
    setNewEntryRegion('');
    setNewEntryAlcohol('');
    if (participantEmail) {
      // Try to fetch display name from DB
      const userRecord = await getUserByEmail(participantEmail);
      setNewEntrySubmittedBy((userRecord && (userRecord.displayName || userRecord.name)) ? (userRecord.displayName || userRecord.name) : '');
    } else {
      setNewEntrySubmittedBy('');
    }
    setDialogOpen(true);
  };

  const handleEdit = async (entry: Entry) => {
    // Only allow edit if user is host or submitted the entry, and competition not started
    if (competitionStatus === 'started' && !isHost) return;
    if (!isHost && entry.submittedByUserId !== user?.id) return;

    console.log('Editing entry:', entry);
    // If host is editing, fetch latest entry data from backend to ensure
    // server-enriched fields like `participantName` and normalized
    // `participantEmail` are available (avoids relying on possibly stale local state).
    let serverEntry: any = null;
    if (isHost && hostUserId) {
      try {
        const res = await fetch(api(`/api/competitions/${hostUserId}/${id}/entries`));
        if (res.ok) {
          const body = await res.json();
          const found = (body.entries || []).find((d: any) => String(d.id) === String(entry.id));
          if (found) serverEntry = found;
        } else {
          console.warn('Failed to refresh entry from server for edit', res.status);
        }
      } catch (e) {
        console.warn('Failed to fetch server entry for edit', e);
      }
    }
    console.debug('handleEdit: serverEntry fetched for', entry.id, serverEntry);
    // Always open in edit mode
    setEditingEntry(entry);
    setNewEntryNameTitle(entry.name);
    setNewEntryDescription(entry.description || '');
    // If host is editing a participant entry, prefer serverEntry values when available
    if (isHost) {
      const src = serverEntry || entry;
      const serverName = (src as any).participantName || (src as any).participantDisplayName || null;
      if (serverName) {
        // Server provided a participant name — treat as authoritative and
        // prefill + disable both fields so host cannot overwrite user data.
        setNewEntrySubmittedBy(serverName);
        setNewEntryParticipantEmail((src as any).participantEmail || '');
        setParticipantNameDisabled(true);
        setParticipantEmailDisabled(true);
        console.debug('handleEdit: using serverName', { serverName, participantEmail: (src as any).participantEmail });
      } else if (src.participantEmail) {
        // Try lookup by email if server didn't provide a name
        const userRecord = await getUserByEmail(src.participantEmail);
        if (userRecord) {
          setNewEntrySubmittedBy(userRecord.displayName || userRecord.name || src.submittedBy || '');
          setNewEntryParticipantEmail(userRecord.email || src.participantEmail || '');
          setParticipantNameDisabled(true);
          setParticipantEmailDisabled(true);
          console.debug('handleEdit: found userRecord by email', { userRecord });
        } else {
          setNewEntrySubmittedBy(src.submittedBy || '');
        }
      } else {
        // No participantEmail provided. If submittedBy looks like an email, try to use it.
        const maybeEmail = typeof src.submittedBy === 'string' && src.submittedBy.includes('@') ? src.submittedBy : null;
        if (maybeEmail) {
          const userRecord = await getUserByEmail(maybeEmail);
          if (userRecord) {
            setNewEntrySubmittedBy(userRecord.displayName || userRecord.name || src.submittedBy || '');
            setNewEntryParticipantEmail(userRecord.email || maybeEmail);
            // authoritative user record found — disable both fields
            setParticipantNameDisabled(true);
            setParticipantEmailDisabled(true);
            console.debug('handleEdit: found userRecord by submittedBy email', { userRecord });
          } else if ((src as any).submittedByUserId) {
            // As a last resort, ask backend for user info by id (avoids client-side permissions issues)
            try {
              const ures = await fetch(api(`/api/users/${(src as any).submittedByUserId}`));
              if (ures.ok) {
                const ubody = await ures.json();
                console.debug('handleEdit: /api/users response', ubody);
                if (ubody) {
                  setNewEntrySubmittedBy(ubody.displayName || src.submittedBy || '');
                  setNewEntryParticipantEmail(ubody.email || maybeEmail || '');
                  setParticipantNameDisabled(Boolean(ubody.displayName));
                  setParticipantEmailDisabled(Boolean(ubody.email));
                }
              }
            } catch (e) {
              // ignore
            }
          } else {
            setNewEntrySubmittedBy(src.submittedBy || '');
            setNewEntryParticipantEmail(maybeEmail);
            setParticipantEmailDisabled(false);
            setParticipantNameDisabled(false);
          }
        } else {
          setNewEntrySubmittedBy(src.submittedBy || '');
          setNewEntryParticipantEmail((src as any).participantEmail || '');
          setParticipantNameDisabled(Boolean((src as any).participantName));
          setParticipantEmailDisabled(Boolean((src as any).participantEmail));
        }
      }
    
    } else {
      setNewEntrySubmittedBy(entry.submittedBy || '');
      setNewEntryParticipantEmail(entry.participantEmail || '');
      setParticipantNameDisabled(false);
      setParticipantEmailDisabled(false);
    }
    // If this entry already has an image, show it in the dialog preview
    if ((entry as any).imageUrl) {
      setTempImageUrl(resolveImageUrl((entry as any).imageUrl) ?? null);
      setTempImagePath(null);
      setImageCleared(false);
    } else if ((entry as any).imagePath) {
      setTempImageUrl(resolveImageUrl(api(`/uploads/${(entry as any).imagePath}`)) ?? null);
      setTempImagePath((entry as any).imagePath);
      setImageCleared(false);
    } else {
      setTempImageUrl(null);
      setTempImagePath(null);
      setImageCleared(false);
    }
    // Populate wine fields when editing
    setNewEntryYear((entry as any).year ?? null);
    setNewEntryWinery((entry as any).winery || '');
    setNewEntryVariety((entry as any).variety || '');
    setNewEntryRegion((entry as any).region || '');
    setNewEntryAlcohol((entry as any).alcohol || '');
    // If the entry has an image but lacks wine metadata, attempt a lookup
    (async () => {
      try {
        const hasImage = Boolean((entry as any).imagePath || (entry as any).imageUrl);
        const lacksMetadata = !((entry as any).winery || (entry as any).variety || (entry as any).year || (entry as any).alcohol || (entry as any).region);
        if (hasImage && lacksMetadata) {
          try {
            const lookupPayload: any = {};
            if ((entry as any).imagePath) lookupPayload.imagePath = (entry as any).imagePath;
            else if ((entry as any).imageUrl) lookupPayload.url = resolveImageUrl((entry as any).imageUrl) || (entry as any).imageUrl;

            const lookupRes = await fetch(api('/api/lookup/wine'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(lookupPayload),
            });
            if (!lookupRes.ok) return;
            const lookupBody = await lookupRes.json();
            let candidate: any = null;
            if (lookupBody) {
              if (Array.isArray(lookupBody.results) && lookupBody.results.length) candidate = lookupBody.results[0];
              else if (Array.isArray(lookupBody.products) && lookupBody.products.length) candidate = lookupBody.products[0];
              else if (Array.isArray(lookupBody) && lookupBody.length) candidate = lookupBody[0];
              else candidate = lookupBody;
            }
            if (candidate) {
              const conf = getCandidateConfidence(candidate);
              if (conf >= CONFIDENCE_THRESHOLD) {
                const prodName = candidate.product_name || candidate.name || candidate.title || candidate.product_name_en || candidate.product_name_fr || candidate.name_en || candidate.name_fr || null;
                if (forceOverwriteOnNextLookup) {
                  setNewEntryNameTitle(prodName || '');
                  setForceOverwriteOnNextLookup(false);
                } else {
                  setNewEntryName(prev => prev || sentenceCase(prodName || ''));
                }
                if (candidate.brands) {
                  if (forceOverwriteOnNextLookup) {
                    setNewEntryWineryTitle(candidate.brands || '');
                    setForceOverwriteOnNextLookup(false);
                  } else {
                    setNewEntryWinery(prev => prev || sentenceCase(candidate.brands || ''));
                  }
                }
                if (candidate.countries) setNewEntryRegion(prev => prev || sentenceCase(candidate.countries || ''));
                if (candidate.nutriments && (candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g)) setNewEntryAlcohol(prev => prev || String(candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g));

                // Use parsing helpers to extract year/variety
                try {
                  const parsed = parseCandidate(candidate);
                  if (parsed.year) {
                    if (forceOverwriteOnNextLookup) {
                      setNewEntryYear(parsed.year);
                      setForceOverwriteOnNextLookup(false);
                    } else {
                      setNewEntryYear(prev => prev || parsed.year);
                    }
                  }
                  if (parsed.variety) {
                    if (forceOverwriteOnNextLookup) {
                      setNewEntryVarietyTitle(parsed.variety);
                      setForceOverwriteOnNextLookup(false);
                    } else {
                      setNewEntryVariety(prev => prev || parsed.variety || '');
                    }
                  }
                } catch (e) { /* ignore */ }
              } else {
                showLowConfidenceToast();
              }
            }
          } catch (e) {
            console.warn('Entry-edit lookup failed', e);
          }
        }
      } catch (e) {
        /* ignore */
      }
    })();
    console.log('Set submittedBy to:', entry.submittedBy);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setNewEntryName('');
    setNewEntryDescription('');
    setNewEntrySubmittedBy('');
    setNewEntryParticipantEmail('');
    // Clear temp/captured images when closing the dialog
    setTempImageUrl(null);
    setTempImagePath(null);
    setCapturedImage(null);
    setImageCleared(false);
    // Clear wine fields when closing
    setNewEntryYear(null);
    setNewEntryWinery('');
    setNewEntryVariety('');
    setNewEntryRegion('');
    setNewEntryAlcohol('');
  };

  const handleSaveEntry = async () => {
    // Ensure we've resolved who the host/owner user id is before creating entries
    if (!hostChecked) {
      setSnackbarMessage('Determining battle owner — please wait a moment');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }
    const effectiveHostId = hostUserId || user?.id;
    if (!effectiveHostId) {
      console.error('No hostUserId available and no current user id');
      setSnackbarMessage('Unable to determine battle owner. Try reloading the page.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!newEntryName.trim()) {
      console.error('No entry name');
      return;
    }

    setSaving(true);
    try {
      // Build payload: when editing an existing entry, do NOT overwrite participant/ownership fields.
      const payload: any = {
        name: newEntryName.trim(),
        description: newEntryDescription.trim(),
        // Wine-specific fields
        year: newEntryYear,
        winery: newEntryWinery.trim(),
        variety: newEntryVariety.trim(),
        region: newEntryRegion.trim(),
        alcohol: newEntryAlcohol.trim(),
        // If a temp image was uploaded, include its server-side temp path so backend can attach it
        tempImagePath: tempImagePath || undefined,
        // If no server path is available but we have a preview URL, include it so backend can persist
        tempImageUrl: tempImageUrl || undefined,
        // If the user explicitly removed an existing image during edit, tell backend to clear it
        ...(editingEntry && imageCleared ? { clearImage: true } : {}),
      };

      // Only include submitter/participant info when creating a new entry.
      if (!editingEntry) {
        payload.submittedBy = isHost ? (user?.displayName || user?.email || 'Host') : (user?.displayName || user?.email || 'Anonymous');
        payload.submittedByUserId = user?.id;
        payload.participantEmail = isHost ? newEntryParticipantEmail.trim() : (user?.email || '');
        payload.participantName = isHost ? newEntrySubmittedBy.trim() : undefined;
      }

      const url = editingEntry ? api(`/api/competitions/${effectiveHostId}/${id}/entries/${editingEntry.id}`) : api(`/api/competitions/${effectiveHostId}/${id}/entries`);
      const method = editingEntry ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Failed to save entry - Response:', errorText);
        setSnackbarMessage('Failed to save entry: ' + errorText);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Read response body and update local state.
      try {
        const body = await res.json();
        if (editingEntry) {
          // Update existing entry in local state
          const updatedEntry: Entry = {
            id: body.id || editingEntry.id,
            name: body.name || newEntryName.trim(),
            description: body.description || newEntryDescription.trim() || '',
            submittedAt: body.submittedAt || editingEntry.submittedAt || null,
            submittedBy: body.submittedBy || editingEntry.submittedBy || (user?.displayName || user?.email || 'Anonymous'),
            submittedByUserId: body.submittedByUserId || editingEntry.submittedByUserId || user?.id,
              enteredByEmail: body.enteredByEmail || (editingEntry as any).enteredByEmail || null,
              participantEmail: body.participantEmail || editingEntry.participantEmail || (user?.email || ''),
              participantName: body.participantName || (editingEntry as any).participantName || undefined,
            randomNumber: body.randomNumber || editingEntry.randomNumber || null,
            publishedAt: body.publishedAt || editingEntry.publishedAt || null,
            year: typeof body.year !== 'undefined' ? body.year : (editingEntry.year ?? newEntryYear),
            winery: body.winery || newEntryWinery || editingEntry.winery || '',
            variety: body.variety || newEntryVariety || editingEntry.variety || '',
            region: body.region || newEntryRegion || editingEntry.region || '',
            alcohol: body.alcohol || newEntryAlcohol || editingEntry.alcohol || '',
            imageUrl: (typeof body.imageUrl !== 'undefined') ? (body.imageUrl || '') : (editingEntry.imageUrl || ''),
            imagePath: (typeof body.imagePath !== 'undefined') ? (body.imagePath || '') : (editingEntry.imagePath || ''),
          } as Entry;

          setEntries(prev => (prev || []).map(e => (e.id === updatedEntry.id ? updatedEntry : e)));
          if (myEntry && myEntry.id === updatedEntry.id) setMyEntry(updatedEntry);
          // Notify user of successful update
          setSnackbarMessage('Entry updated');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else if (body && body.id) {
          // Creating a new entry — optimistic add
          const optimisticEntry: Entry = {
            id: body.id,
            name: body.name || newEntryName.trim(),
            description: body.description || newEntryDescription.trim() || '',
            submittedAt: body.submittedAt || null,
            submittedBy: body.submittedBy || (user?.displayName || user?.email || 'Anonymous'),
            submittedByUserId: body.submittedByUserId || user?.id,
            enteredByEmail: body.enteredByEmail || user?.email || null,
            participantEmail: body.participantEmail || (user?.email || ''),
            participantName: body.participantName || undefined,
            randomNumber: body.randomNumber || null,
            publishedAt: body.publishedAt || null,
            year: typeof body.year !== 'undefined' ? body.year : newEntryYear,
            winery: body.winery || newEntryWinery || '',
            variety: body.variety || newEntryVariety || '',
            region: body.region || newEntryRegion || '',
            alcohol: body.alcohol || newEntryAlcohol || '',
            imageUrl: body.imageUrl || '',
            imagePath: body.imagePath || '',
          } as Entry;

          // Only set `myEntry` if the created entry belongs to the current user
          if (optimisticEntry.participantEmail && user?.email && optimisticEntry.participantEmail.trim().toLowerCase() === user.email.trim().toLowerCase()) {
            setMyEntry(optimisticEntry);
          } else if (optimisticEntry.submittedByUserId && user?.id && optimisticEntry.submittedByUserId === user.id) {
            setMyEntry(optimisticEntry);
          }

          // Avoid inserting duplicate entries if the same id already exists in state
          setEntries(prev => {
            const exists = (prev || []).some(e => e.id === optimisticEntry.id);
            if (exists) return prev || [];
            return [optimisticEntry, ...(prev || [])];
          });

          // Notify user of successful save
          setSnackbarMessage('Entry saved');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }
      } catch (e) {
        // ignore optimistic update failures
      }

      // Reset form and close dialog
      setDialogOpen(false);
      setEditingEntry(null);
      setNewEntryName('');
      setNewEntryDescription('');
      setNewEntrySubmittedBy('');
      setNewEntryParticipantEmail('');
      setImageCleared(false);
    } catch (error) {
      console.error('Error saving entry:', error);
      setSnackbarMessage('Error saving entry: ' + String(error));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const uploadTempImage = async (dataUrl: string, options?: { skipLookup?: boolean }) => {
    if (!dataUrl) throw new Error('No image to upload');

    // smallDataUrl used for immediate local preview; declare in function scope
    let smallDataUrl: string | undefined = undefined;

    // Attempt to auto-crop the label region to improve lookup accuracy.
    const canvasToDataUrl = (c: HTMLCanvasElement, quality = 0.95) => {
      return new Promise<string>((resolve) => {
        c.toBlob((blob) => {
          if (!blob) return resolve(c.toDataURL('image/jpeg', quality));
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result || c.toDataURL('image/jpeg', quality)));
          reader.readAsDataURL(blob);
        }, 'image/jpeg', quality);
      });
    };

    const autoCropLabel = async (srcDataUrl: string) => {
      try {
        const img = new Image();
        img.src = srcDataUrl;
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return srcDataUrl;
        // Draw at native resolution and disable smoothing to preserve detail
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, w, h);
        const imgData = ctx.getImageData(0, 0, w, h);
        const data = imgData.data;

        // Find bounding box of pixels that are not near-white (label content)
        const threshold = 250; // luminance threshold (0-255)
        let minX = w, minY = h, maxX = 0, maxY = 0;
        for (let y = 0; y < h; y += 2) { // sample every 2nd row for speed
          for (let x = 0; x < w; x += 2) {
            const idx = (y * w + x) * 4;
            const r = data[idx], g = data[idx + 1], b = data[idx + 2], a = data[idx + 3];
            if (a === 0) continue;
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;
            if (lum < threshold) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        // If we didn't find anything significant, try sampling saturation instead
        if (minX > maxX || minY > maxY) {
          for (let y = 0; y < h; y += 4) {
            for (let x = 0; x < w; x += 4) {
              const idx = (y * w + x) * 4;
              const r = data[idx] / 255, g = data[idx + 1] / 255, b = data[idx + 2] / 255;
              const max = Math.max(r, g, b), min = Math.min(r, g, b);
              const sat = max === 0 ? 0 : (max - min) / max;
              if (sat > 0.15) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
        }

        // If still nothing found, return original
        if (minX > maxX || minY > maxY) return srcDataUrl;

        // Add padding
        const padX = Math.round((maxX - minX) * 0.15);
        const padY = Math.round((maxY - minY) * 0.15);
        const sx = Math.max(0, minX - padX);
        const sy = Math.max(0, minY - padY);
        const sw = Math.min(w - sx, (maxX - minX) + padX * 2);
        const sh = Math.min(h - sy, (maxY - minY) + padY * 2);

        // Ensure reasonable aspect; expand if too narrow
        const aspect = sw / sh;
        let finalSx = sx, finalSy = sy, finalSw = sw, finalSh = sh;
        if (aspect < 0.5) {
          const desiredSw = Math.min(w, Math.round(sh * 0.7));
          const extra = desiredSw - sw;
          finalSx = Math.max(0, sx - Math.round(extra / 2));
          finalSw = Math.min(w - finalSx, desiredSw);
        } else if (aspect > 2.5) {
          const desiredSh = Math.min(h, Math.round(sw / 1.5));
          const extra = desiredSh - sh;
          finalSy = Math.max(0, sy - Math.round(extra / 2));
          finalSh = Math.min(h - finalSy, desiredSh);
        }

        const outCanvas = document.createElement('canvas');
        outCanvas.width = finalSw;
        outCanvas.height = finalSh;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return srcDataUrl;
        outCtx.imageSmoothingEnabled = false;
        outCtx.drawImage(canvas, finalSx, finalSy, finalSw, finalSh, 0, 0, finalSw, finalSh);
        try {
          return await canvasToDataUrl(outCanvas, 0.95);
        } catch (e) {
          return srcDataUrl;
        }
      } catch (e) {
        console.warn('Auto-crop failed, using original image', e);
        return srcDataUrl;
      }
    };

    // perform auto-crop but don't block on it failing
    try {
      dataUrl = await autoCropLabel(dataUrl);
    } catch (e) { console.warn('Auto-crop error', e); }
    try {
      // Resize large captures client-side to reduce payload size and avoid proxy limits
      const resizeDataUrl = async (src: string, maxWidth = 1200, quality = 0.95) => {
        return await new Promise<string>((resolve) => {
          const img = new Image();
          img.onload = async () => {
            const imgW = img.naturalWidth || img.width;
            const imgH = img.naturalHeight || img.height;
            const scale = Math.min(1, maxWidth / imgW);
            const w = Math.round(imgW * scale);
            const h = Math.round(imgH * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.imageSmoothingEnabled = false;
              ctx.drawImage(img, 0, 0, w, h);
            }
            try {
              const out = await canvasToDataUrl(canvas, quality);
              resolve(out);
            } catch (e) {
              // Fallback: return original
              resolve(src);
            }
          };
          img.onerror = () => resolve(src);
          img.src = src;
        });
      };

      // Generate multiple scales for lookup (multi-scale improves recognition)
      const scales = [600, 1200, 1800];
      const resizePromises = scales.map((w) => resizeDataUrl(dataUrl, w, 0.9));
      const resizedDataUrls = await Promise.all(resizePromises);
      // Small preview data URL (use smallest resized image for immediate preview)
      smallDataUrl = resizedDataUrls && resizedDataUrls.length ? resizedDataUrls[0] : dataUrl;

      // Helper: post a single dataUrl to label lookup proxy and return products
      const lookupDataUrl = async (durl: string) => {
        try {
          const b = await (await fetch(durl)).blob();
          const form = new FormData();
          form.append('image', b, 'lookup.jpg');
          const res = await fetch(api('/api/lookup/wine/label'), { method: 'POST', body: form });
          if (!res.ok) return null;
          return await res.json();
        } catch (e) {
          console.warn('Lookup scale request failed', e);
          return null;
        }
      };

      // Run lookups sequentially (avoid concurrent requests that can trigger upstream throttling)
      let scaleLookupResults = [] as any[];
      try {
        for (const d of resizedDataUrls) {
          const r = await lookupDataUrl(d);
          if (!r) continue;
          if (Array.isArray(r.products) && r.products.length) {
            scaleLookupResults.push(...r.products);
            // stop after the first provider result with candidates to reduce load
            break;
          }
          if (Array.isArray(r)) scaleLookupResults.push(...r);
          else if (r && r.products && Array.isArray(r.products)) scaleLookupResults.push(...r.products);
        }
      } catch (e) {
        console.warn('Multi-scale lookup (sequential) failed', e);
      }

      // Aggregate candidates by name -> best score and track best product
      const agg: Record<string, number> = {};
      let bestProduct: any = null;
      let bestScore = 0;
      for (const p of scaleLookupResults) {
        try {
          const name = String(p.name || p.product_name || p.title || '').trim();
          const score = Number(p.score || p.confidence || p[1] || 0) || 0;
          if (!name) continue;
          const key = name.toLowerCase();
          agg[key] = Math.max(agg[key] || 0, score);
          if (score > bestScore) { bestScore = score; bestProduct = p; }
        } catch (e) { /* ignore */ }
      }
      const sorted = Object.entries(agg).sort((a, b) => b[1] - a[1]);
      let topCandidateName: string | null = null;
      if (sorted.length) topCandidateName = sorted[0][0];

      // Apply top candidate only if confidence exceeds threshold
      const normalizedBestScore = normalizeConfidence(bestScore);
      if (bestProduct && normalizedBestScore >= CONFIDENCE_THRESHOLD) {
        const prodName = bestProduct.name || bestProduct.product_name || '';
        if (prodName) {
          if (forceOverwriteOnNextLookup) {
            setNewEntryNameTitle(prodName);
            setForceOverwriteOnNextLookup(false);
          } else {
            setNewEntryName(prev => prev || sentenceCase(prodName));
          }
        }
        // Also apply some common fields if present
        try {
          if (bestProduct.brands) {
            if (forceOverwriteOnNextLookup) {
              setNewEntryWineryTitle(bestProduct.brands || '');
              setForceOverwriteOnNextLookup(false);
            } else {
              setNewEntryWinery(prev => prev || sentenceCase(bestProduct.brands || ''));
            }
          }
          if (bestProduct.categories) {
            if (forceOverwriteOnNextLookup) {
              setNewEntryVarietyTitle(bestProduct.categories || '');
              setForceOverwriteOnNextLookup(false);
            } else {
              setNewEntryVariety(prev => prev || sentenceCase(bestProduct.categories || ''));
            }
          }
          if (bestProduct.countries) setNewEntryRegion(prev => prev || sentenceCase(bestProduct.countries || ''));
          if (bestProduct.nutriments && (bestProduct.nutriments.alcohol || bestProduct.nutriments.alcohol_100g)) setNewEntryAlcohol(prev => prev || String(bestProduct.nutriments.alcohol || bestProduct.nutriments.alcohol_100g));
        } catch (e) { console.warn('Failed to apply bestProduct fields', e); }
        try {
          const parsed = parseCandidate(bestProduct);
          if (parsed.year) {
            if (forceOverwriteOnNextLookup) {
              setNewEntryYear(parsed.year);
              setForceOverwriteOnNextLookup(false);
            } else {
              setNewEntryYear(prev => prev || parsed.year);
            }
          }
          if (parsed.variety) {
            if (forceOverwriteOnNextLookup) {
              setNewEntryVarietyTitle(parsed.variety);
              setForceOverwriteOnNextLookup(false);
            } else {
              setNewEntryVariety(prev => prev || parsed.variety || '');
            }
          }
        } catch (e) { /* ignore */ }
      }
      else if (bestProduct) {
        // best product found but confidence too low
        showLowConfidenceToast();
      }

      // For upload, prefer the largest resized (or original) to preserve detail
      const chosenDataUrl = resizedDataUrls[resizedDataUrls.length - 1] || dataUrl;
      const blob = await (await fetch(chosenDataUrl)).blob();

      // Try signed-upload flow first to avoid edge/proxy multipart limits
      try {
        const fname = 'capture.jpg';
        const signedRes = await fetch(api('/api/uploads/signed'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: fname, mimetype: blob.type || 'image/jpeg', folder: 'temp' }),
        });
        if (signedRes.ok) {
          const signedBody = await signedRes.json();
          if (signedBody && signedBody.signedUrl) {
            // PUT the blob directly to GCS
            const putRes = await fetch(signedBody.signedUrl, { method: 'PUT', headers: { 'Content-Type': blob.type || 'image/jpeg' }, body: blob });
            if (!putRes.ok) throw new Error('Signed upload PUT failed: ' + putRes.status);

            // Use returned imagePath and prefer a local preview (smallDataUrl) for immediate display
            const imagePath = signedBody.imagePath;
            const imageUrl = signedBody.publicUrl || (`https://storage.googleapis.com/${signedBody.bucket}/${imagePath}`);
            setTempImagePath(imagePath);
            // Prefer the client-side resized data URL for preview so browsers don't show a broken image
            setTempImageUrl(smallDataUrl || imageUrl);

            // Trigger lookup using server-side imagePath (only for wine lookups)
            if (!options?.skipLookup) {
              try {
                const lookupRes = await fetch(api('/api/lookup/wine'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ imagePath }),
                });
                if (lookupRes.ok) {
                  const lookupBody = await lookupRes.json();
                  // Apply candidate heuristics (reuse existing logic)
                  let candidate: any = null;
                  if (lookupBody) {
                    if (Array.isArray(lookupBody.results) && lookupBody.results.length) candidate = lookupBody.results[0];
                    else if (Array.isArray(lookupBody.products) && lookupBody.products.length) candidate = lookupBody.products[0];
                    else if (Array.isArray(lookupBody) && lookupBody.length) candidate = lookupBody[0];
                    else candidate = lookupBody;
                  }
                  if (candidate) {
                    // Apply candidate fields consistently (name, winery, variety, region, alcohol, year)
                    applyLookupCandidate(candidate, forceOverwriteOnNextLookup);
                    // If confidence is low, notify user but still populated basic fields
                    const conf = getCandidateConfidence(candidate);
                    if (conf < CONFIDENCE_THRESHOLD) showLowConfidenceToast();
                  }
                }
              } catch (e) {
                console.warn('Lookup after signed upload failed', e);
              }
            }

            return { imagePath, imageUrl };
          }
        }
      } catch (e) {
        console.warn('Signed upload flow failed, falling back to multipart/form-data', e);
      }

      const form = new FormData();
      form.append('image', blob, 'bottle.jpg');
      const res = await fetch(api('/api/uploads/temp'), { method: 'POST', body: form });
      if (!res.ok) {
        // If multipart upload fails (edge may block FormData), attempt JSON/base64 fallback
        try {
          const raw = await (await fetch(dataUrl)).arrayBuffer();
          // Browser-friendly ArrayBuffer -> base64
          const abToBase64 = (buffer: ArrayBuffer) => {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
            return typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
          };
          const b64 = abToBase64(raw);
          const jsonRes = await fetch(api('/api/uploads/temp'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ base64: b64, filename: 'capture.jpg', mimetype: 'image/jpeg' }),
          });
          if (!jsonRes.ok) {
            const txt = await jsonRes.text();
            throw new Error('Temp upload JSON fallback failed: ' + txt);
          }
          const body = await jsonRes.json();
          // reuse success path below
          if (body && body.imagePath) {
            setTempImagePath(body.imagePath);
            let imageUrl = body.imageUrl || api(`/uploads/${body.imagePath}`);
            if (typeof imageUrl === 'string' && imageUrl.startsWith('/')) {
              try {
                let origin = window.location.origin;
                if (typeof API_BASE === 'string' && API_BASE.startsWith('http')) {
                  origin = new URL(API_BASE).origin;
                }
                imageUrl = `${origin}${imageUrl}`;
              } catch (e) { /* fallback */ }
            }
            setTempImageUrl(imageUrl);

            // Trigger lookup using server-side imagePath
            try {
              const lookupRes = await fetch(api('/api/lookup/wine'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imagePath: body.imagePath }),
              });
              if (lookupRes.ok) {
                const lookupBody = await lookupRes.json();
                // Apply candidate heuristics (reuse existing logic)
                let candidate: any = null;
                if (lookupBody) {
                  if (Array.isArray(lookupBody.results) && lookupBody.results.length) candidate = lookupBody.results[0];
                  else if (Array.isArray(lookupBody.products) && lookupBody.products.length) candidate = lookupBody.products[0];
                  else if (Array.isArray(lookupBody) && lookupBody.length) candidate = lookupBody[0];
                  else candidate = lookupBody;
                }
                if (candidate) {
                  const prodName = candidate.product_name || candidate.name || candidate.title || candidate.product_name_en || candidate.product_name_fr || candidate.name_en || candidate.name_fr || null;
                  if (forceOverwriteOnNextLookup) {
                    setNewEntryNameTitle(prodName || '');
                    setForceOverwriteOnNextLookup(false);
                  } else {
                    setNewEntryName(prev => prev || sentenceCase(prodName || ''));
                  }
                  if (candidate.brands) {
                    if (forceOverwriteOnNextLookup) {
                      setNewEntryWineryTitle(candidate.brands || '');
                      setForceOverwriteOnNextLookup(false);
                    } else {
                      setNewEntryWinery(prev => prev || sentenceCase(candidate.brands || ''));
                    }
                  }
                  if (candidate.countries) setNewEntryRegion(prev => prev || sentenceCase(candidate.countries || ''));
                  if (candidate.nutriments && (candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g)) setNewEntryAlcohol(prev => prev || String(candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g));
                  const textSource = prodName || (candidate.products && candidate.products[0] && candidate.products[0].name) || (candidate.classes && candidate.classes[0] && (candidate.classes[0].name || candidate.classes[0])) || (typeof candidate === 'string' ? candidate : undefined) || '';
                  const txt = String(textSource || '').trim();
                  if (txt) {
                    const yearMatch = txt.match(/\b(19|20)\d{2}\b/);
                    if (yearMatch && !newEntryYear) setNewEntryYear(Number(yearMatch[0]));
                    const varietals = ['sauvignon blanc','pinot noir','merlot','cabernet sauvignon','riesling','chardonnay','syrah','shiraz','zinfandel','malbec','gewurztraminer','grenache','barbera','tempranillo'];
                    const lower = txt.toLowerCase();
                    for (const v of varietals) {
                      if (lower.includes(v) && !newEntryVariety) {
                        const title = v.split(' ').map(s => s.charAt(0).toUpperCase()+s.slice(1)).join(' ');
                        setNewEntryVariety(title);
                        break;
                      }
                    }
                    if (!newEntryWinery && prodName) {
                      let wineryGuess = prodName;
                      if (yearMatch) wineryGuess = wineryGuess.replace(yearMatch[0], '');
                      for (const v of varietals) wineryGuess = wineryGuess.replace(new RegExp(v, 'ig'), '');
                      wineryGuess = wineryGuess.replace(/[^a-zA-Z0-9\s\-\/\,\&]/g, '');
                      wineryGuess = wineryGuess.replace(/\s{2,}/g, ' ').trim();
                      if (wineryGuess && wineryGuess.length > 1 && wineryGuess.toLowerCase() !== txt.toLowerCase()) {
                          setNewEntryWinery(prev => prev || sentenceCase(wineryGuess));
                        }
                    }
                  }
                }
              }
            } catch (e) {
              console.warn('Lookup after JSON upload failed', e);
            }
            return body;
          }
        } catch (e) {
          const txt = await res.text().catch(() => '');
          throw new Error('Temp upload failed: ' + txt + ' (and JSON fallback error: ' + String(e) + ')');
        }
        // end JSON fallback
      }
      const body = await res.json();
      if (body && body.imagePath) {
        setTempImagePath(body.imagePath);
        let imageUrl = body.imageUrl || api(`/uploads/${body.imagePath}`);
        // If backend returned a relative path (starts with '/'), convert to absolute
        if (typeof imageUrl === 'string' && imageUrl.startsWith('/')) {
          try {
            let origin = window.location.origin;
            if (typeof API_BASE === 'string' && API_BASE.startsWith('http')) {
              origin = new URL(API_BASE).origin;
            }
            imageUrl = `${origin}${imageUrl}`;
          } catch (e) {
            // fallback: leave relative URL
          }
        }
        setTempImageUrl(imageUrl);

        // Run image-based wine lookup to auto-populate wine fields when possible
        (async () => {
          try {
            const lookupRes = await fetch(api('/api/lookup/wine'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ imagePath: body.imagePath }),
            });
            if (!lookupRes.ok) return;
            const lookupBody = await lookupRes.json();
            // Results shape varies by provider; try several common fields
            let candidate: any = null;
            if (lookupBody) {
              if (Array.isArray(lookupBody.results) && lookupBody.results.length) candidate = lookupBody.results[0];
              else if (Array.isArray(lookupBody.products) && lookupBody.products.length) candidate = lookupBody.products[0];
              else if (Array.isArray(lookupBody) && lookupBody.length) candidate = lookupBody[0];
              else candidate = lookupBody;
            }
            if (candidate) {
              try {
                // Prefer an explicit product/title if present
                const prodName = candidate.product_name || candidate.name || candidate.title || candidate.product_name_en || candidate.product_name_fr || candidate.name_en || candidate.name_fr || null;
                setNewEntryName(prev => prev || sentenceCase(prodName || ''));

                // Winery: explicit brands field is best
                if (candidate.brands) setNewEntryWinery(prev => prev || sentenceCase(candidate.brands || ''));

                // Alcohol / region
                if (candidate.countries) setNewEntryRegion(prev => prev || sentenceCase(candidate.countries || ''));
                if (candidate.nutriments && (candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g)) setNewEntryAlcohol(prev => prev || String(candidate.nutriments.alcohol || candidate.nutriments.alcohol_100g));

                // Variety/year heuristics: api4 often returns class/product names like "Paua Bay Sauvignon Blanc 2008"
                const textSource = prodName || (candidate.products && candidate.products[0] && candidate.products[0].name) || (candidate.classes && candidate.classes[0] && (candidate.classes[0].name || candidate.classes[0])) || (typeof candidate === 'string' ? candidate : undefined) || '';
                const txt = String(textSource || '').trim();
                if (txt) {
                  // Year extraction
                  const yearMatch = txt.match(/\b(19|20)\d{2}\b/);
                  if (yearMatch && !newEntryYear) setNewEntryYear(Number(yearMatch[0]));

                  // Variety extraction: match common varietal names
                  const varietals = ['sauvignon blanc','pinot noir','merlot','cabernet sauvignon','riesling','chardonnay','syrah','shiraz','zinfandel','malbec','gewurztraminer','grenache','barbera','tempranillo'];
                  const lower = txt.toLowerCase();
                  for (const v of varietals) {
                      if (lower.includes(v) && !newEntryVariety) {
                      // Title-case the varietal
                      const title = v.split(' ').map(s => s.charAt(0).toUpperCase()+s.slice(1)).join(' ');
                      setNewEntryVariety(prev => prev || title);
                      break;
                    }
                  }

                  // Winery heuristic: if prodName contains a varietal and/or year, remove them to get probable winery
                  if (!newEntryWinery && prodName) {
                    let wineryGuess = prodName;
                    if (yearMatch) wineryGuess = wineryGuess.replace(yearMatch[0], '');
                    for (const v of varietals) wineryGuess = wineryGuess.replace(new RegExp(v, 'ig'), '');
                    wineryGuess = wineryGuess.replace(/[^a-zA-Z0-9\s\-\/\,\&]/g, '');
                    wineryGuess = wineryGuess.replace(/\s{2,}/g, ' ').trim();
                    if (wineryGuess && wineryGuess.length > 1 && wineryGuess.toLowerCase() !== txt.toLowerCase()) {
                      setNewEntryWineryTitle(wineryGuess);
                    }
                  }
                }

                // Fallbacks from provider arrays
                if (!newEntryVariety && candidate.products && Array.isArray(candidate.products) && candidate.products.length) {
                  const first = candidate.products[0];
                  if (first.name) setNewEntryVariety(prev => prev || sentenceCase(String(first.name)));
                }
                if (!newEntryVariety && candidate.classes && Array.isArray(candidate.classes) && candidate.classes.length) {
                  setNewEntryVariety(prev => prev || sentenceCase(String(candidate.classes[0].name || candidate.classes[0])));
                }
              } catch (e) {
                console.warn('Failed to apply lookup candidate to wine fields', e);
              }
            }
          } catch (e) {
            console.warn('Image lookup failed', e);
          }
        })();

        return body;
      }
      throw new Error('Invalid temp upload response');
    } catch (e) {
      console.error('uploadTempImage failed', e);
      throw e;
    }
  };

  const handleStartCompetition = async () => {
    const effectiveHostId = hostUserId || user?.id;
    if (!effectiveHostId) {
      console.error('No hostUserId available to start battle');
      setSnackbarMessage('Unable to determine battle owner — try reloading');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      console.log('Starting battle:', { hostUserId: effectiveHostId, competitionId: id });
      const res = await fetch(api(`/api/competitions/${effectiveHostId}/${id}/start`), {
        method: 'POST',
      });

      console.log('Start response:', res.status, res.ok);

      if (res.ok) {
        console.log('Battle started successfully');
        // Prevent transient "Publish Changes" UI while we refresh server state
        setSuppressUnpublished(true);
        // Immediately update local state so host UI reflects started status without a full refresh
        setCompetitionStatus('started');

        // Refresh competition metadata, entries and votes so host UI is consistent.
        try {
          try {
            const compRes = await fetch(api(`/api/competitions/${effectiveHostId}/${id}`));
            if (compRes.ok) {
              const compBody = await compRes.json();
              if (compBody) {
                setLastPublishedAt(compBody.lastPublishedAt || null);
                setMaxVotesPerPerson(compBody.maxVotesPerPerson || 3);
              }
            }
            } catch (e) {
            console.warn('Failed to refresh battle after start', e);
          }

          try {
            const entriesRes = await fetch(api(`/api/competitions/${effectiveHostId}/${id}/entries`));
            if (entriesRes.ok) {
              const entriesBody = await entriesRes.json();
              const entriesData = (entriesBody.entries || []).map((doc: any) => ({
                id: doc.id,
                name: doc.name,
                description: doc.description || '',
                submittedAt: doc.submittedAt,
                submittedBy: doc.submittedBy,
                submittedByUserId: doc.submittedByUserId,
                enteredByEmail: doc.enteredByEmail || null,
                participantEmail: doc.participantEmail,
                participantName: doc.participantName || doc.participantDisplayName || undefined,
                randomNumber: doc.randomNumber,
                publishedAt: doc.publishedAt,
                year: typeof doc.year !== 'undefined' ? doc.year : null,
                winery: doc.winery || '',
                variety: doc.variety || '',
                region: doc.region || '',
                alcohol: doc.alcohol || '',
                imageUrl: doc.imageUrl || '',
                imagePath: doc.imagePath || '',
              })) as Entry[];
              setEntries(entriesData);
              // recompute myEntry quickly
              try {
                if (user?.email) {
                  const normalizedEmail = user.email.trim().toLowerCase();
                  const found = entriesData.find((e: Entry) => {
                    const pe = (e.participantEmail || '') as string;
                    return pe && pe.trim().toLowerCase() === normalizedEmail || (e.submittedByUserId && e.submittedByUserId === user.id);
                  });
                  if (found) setMyEntry(found);
                }
              } catch (e) { /* ignore */ }
            }
          } catch (e) {
            console.warn('Failed to refresh entries after start', e);
          }

          // Refresh votes immediately for host view
          try {
            const votesRes = await fetch(api(`/api/competitions/${effectiveHostId}/${id}/votes`));
            if (votesRes.ok) {
              const votesBody = await votesRes.json();
              setAllVotes(votesBody.votes || {});
            }
          } catch (e) {
            console.warn('Failed to refresh votes after start', e);
          }
        } finally {
          // Allow the UI to show publish state again now that we've refreshed server state
          setSuppressUnpublished(false);
        }
      } else {
        const errorText = await res.text();
        console.error('Failed to start battle:', errorText);
        setSnackbarMessage('Failed to start battle: ' + errorText);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to start battle:', error);
      setSnackbarMessage('Failed to start battle');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePublishChanges = async () => {
    if (!hostUserId || !isHost) return;

    try {
      console.log('Publishing changes to battle');
      const res = await fetch(api(`/api/competitions/${hostUserId}/${id}/publish`), {
        method: 'POST',
      });

      if (res.ok) {
        console.log('Changes published successfully');
        // Prevent transient "Publish Changes" UI while we refresh server state
        setSuppressUnpublished(true);

        // Refresh competition metadata, entries and votes so host UI reflects published state
        try {
          try {
            const compRes = await fetch(api(`/api/competitions/${hostUserId}/${id}`));
            if (compRes.ok) {
              const compBody = await compRes.json();
              if (compBody) {
                setLastPublishedAt(compBody.lastPublishedAt || null);
                setMaxVotesPerPerson(compBody.maxVotesPerPerson || 3);
              }
            }
          } catch (e) {
            console.warn('Failed to refresh battle after publish', e);
          }

          try {
            const entriesRes = await fetch(api(`/api/competitions/${hostUserId}/${id}/entries`));
            if (entriesRes.ok) {
              const entriesBody = await entriesRes.json();
              const entriesData = (entriesBody.entries || []).map((doc: any) => ({
                id: doc.id,
                name: doc.name,
                description: doc.description || '',
                submittedAt: doc.submittedAt,
                submittedBy: doc.submittedBy,
                submittedByUserId: doc.submittedByUserId,
                enteredByEmail: doc.enteredByEmail || null,
                participantEmail: doc.participantEmail,
                participantName: doc.participantName || doc.participantDisplayName || undefined,
                randomNumber: doc.randomNumber,
                publishedAt: doc.publishedAt,
                year: typeof doc.year !== 'undefined' ? doc.year : null,
                winery: doc.winery || '',
                variety: doc.variety || '',
                region: doc.region || '',
                alcohol: doc.alcohol || '',
                imageUrl: doc.imageUrl || '',
                imagePath: doc.imagePath || '',
              })) as Entry[];
              setEntries(entriesData);
              // update myEntry quickly
              try {
                if (user?.email) {
                  const normalizedEmail = user.email.trim().toLowerCase();
                  const found = entriesData.find((e: Entry) => {
                    const pe = (e.participantEmail || '') as string;
                    return pe && pe.trim().toLowerCase() === normalizedEmail || (e.submittedByUserId && e.submittedByUserId === user.id);
                  });
                  if (found) setMyEntry(found);
                }
              } catch (e) { /* ignore */ }
            }
          } catch (e) {
            console.warn('Failed to refresh entries after publish', e);
          }

          try {
            const votesRes = await fetch(api(`/api/competitions/${hostUserId}/${id}/votes`));
            if (votesRes.ok) {
              const votesBody = await votesRes.json();
              setAllVotes(votesBody.votes || {});
            }
          } catch (e) {
            console.warn('Failed to refresh votes after publish', e);
          }
        } finally {
          // Allow publish UI to re-evaluate now that we've refreshed server state
          setSuppressUnpublished(false);
        }

        setSnackbarMessage('Changes published to participants!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorText = await res.text();
        console.error('Failed to publish changes:', errorText);
        setSnackbarMessage('Failed to publish changes');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error publishing changes:', error);
      setSnackbarMessage('Error publishing changes');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleStopCompetition = async () => {
    if (!hostUserId || !isHost) return;

    try {
      const res = await fetch(api(`/api/competitions/${hostUserId}/${id}/stop`), { method: 'POST' });
      if (res.ok) {
        console.log('Voting stopped');
        // real-time listener will update status; also set locally for immediate UI feedback
        setCompetitionStatus('stopped');
        setSnackbarMessage('Voting has been stopped. Participants can no longer change votes.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      } else {
        const errorText = await res.text();
        console.error('Failed to stop voting:', errorText);
        setSnackbarMessage('Failed to stop voting');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error stopping voting:', error);
      setSnackbarMessage('Error stopping voting');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePublishResults = async () => {
    if (!hostUserId || !isHost) return;

    try {
      const res = await fetch(api(`/api/competitions/${hostUserId}/${id}/publish-results`), { method: 'POST' });
      if (res.ok) {
        const body = await res.json();
        console.log('Publish results response:', body);
        setCompetitionStatus('published');
        setResults(body.results || null);
        setSnackbarMessage('Results published to participants');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorText = await res.text();
        console.error('Failed to publish results:', errorText);
        setSnackbarMessage('Failed to publish results');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error publishing results:', error);
      setSnackbarMessage('Error publishing results');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const [results, setResults] = useState<any | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!hostUserId || !id || !user?.id) return;
      try {
        const res = await fetch(api(`/api/competitions/${hostUserId}/${id}/results/${user.id}`));
        if (res.ok) {
          const body = await res.json();
          setResults(body.results);
        } else {
          console.log('Results not available for this user yet');
        }
      } catch (e) {
        console.error('Failed to fetch results:', e);
      }
    };

    if (competitionStatus === 'published') fetchResults();
  }, [competitionStatus, hostUserId, id, user?.id]);

  const handleSaveNotes = useCallback(async (entryId: string, notes: string) => {
    if (!user?.id) return;

    // Clear existing timeout for this entry
    if (debounceTimeout.current[entryId]) {
      clearTimeout(debounceTimeout.current[entryId]);
    }

    // Update local state immediately
    setEntryNotes(prev => ({ ...prev, [entryId]: notes }));

    // Debounce the API call
    debounceTimeout.current[entryId] = setTimeout(async () => {
      try {
        // Save under the participant's own path; include hostId so backend can reference the host competition if needed
        await fetch(api(`/api/competitions/${user?.id}/${id}/entries/${entryId}/notes`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes, hostId: hostUserId }),
        });
      } catch (error) {
        console.error('Failed to save notes:', error);
      }
    }, 500); // Wait 500ms after user stops typing
  }, [user?.id, id]);
  

  const handleAddVote = (entryId: string) => {
    setVotesByEntry(prev => {
      const currentVotes = prev[entryId] || 0;
      if (totalVotesUsed >= maxVotesPerPerson) {
        return prev; // Can't add more votes
      }
      return {
        ...prev,
        [entryId]: currentVotes + 1,
      };
    });
  };

  const handleRemoveVote = (entryId: string) => {
    setVotesByEntry(prev => {
      const currentVotes = prev[entryId] || 0;
      if (currentVotes === 0) return prev;
      
      const newVotes = { ...prev };
      if (currentVotes === 1) {
        delete newVotes[entryId];
      } else {
        newVotes[entryId] = currentVotes - 1;
      }
      return newVotes;
    });
  };

  // Keep track of whether votes/notes differ from last saved state
  const mapsEqual = (a: { [k: string]: number }, b: { [k: string]: number }) => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if ((a[k] || 0) !== (b[k] || 0)) return false;
    }
    return true;
  };

  useEffect(() => {
    const changed = !mapsEqual(votesByEntry || {}, lastSavedVotes || {}) || (String(votingNotes || '') !== String(lastSavedVotingNotes || ''));
    setVotesChanged(changed);
  }, [votesByEntry, votingNotes, lastSavedVotes, lastSavedVotingNotes]);

  const handleToggleVote = (entryId: string) => {
    if (maxVotesPerPerson === 1) {
      // Radio button behavior - only one selection
      setVotesByEntry({ [entryId]: 1 });
    }
  };

  const handleSaveVote = async () => {
    if (!user?.id || !hostUserId) return;

    try {
      // Convert votesByEntry object to array of entryIds
      const entryIds: string[] = [];
      Object.entries(votesByEntry).forEach(([entryId, count]) => {
        for (let i = 0; i < count; i++) {
          entryIds.push(entryId);
        }
      });

      console.log('Submitting votes:', { entryIds, hostUserId });

      const response = await fetch(
        api(`/api/competitions/${user.id}/${id}/votes`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entryIds: entryIds,
            hostId: hostUserId,
          }),
        }
      );

      console.log('Vote response:', response.status, response.ok);

      if (response.ok) {
        setHasVoted(true);
        // After successful save, update lastSaved state so button becomes disabled until next change
        setLastSavedVotes(votesByEntry || {});
        setLastSavedVotingNotes(votingNotes || '');
        setVotesChanged(false);
        setSnackbarMessage('Your votes have been saved!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorText = await response.text();
        console.log('Failed to save vote:', errorText);
        setSnackbarMessage('Failed to save votes. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving vote:', error);
      setSnackbarMessage('Failed to save votes. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    // Use resolved host user id when available; otherwise fallback to current user
    const effectiveHostId = hostUserId || user?.id;
    if (!effectiveHostId) {
      console.error('No host user id available to delete entry');
      setSnackbarMessage('Unable to determine battle owner for delete');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const res = await fetch(
        api(`/api/competitions/${effectiveHostId}/${id}/entries/${entryId}`),
        { method: 'DELETE' }
      );

      if (res.ok) {
        // Update local state immediately since we no longer rely on client realtime listeners
        setEntries(prev => (prev || []).filter(e => e.id !== entryId));
        if (myEntry && myEntry.id === entryId) setMyEntry(null);
        setSnackbarMessage('Entry deleted');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const txt = await res.text();
        console.error('Failed to delete entry - server responded:', txt);
        setSnackbarMessage('Failed to delete entry: ' + txt);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to delete entry:', error);
      setSnackbarMessage('Error deleting entry: ' + String(error));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRequestDelete = (entryId: string, submittedByUserId?: string) => {
    if (competitionStatus === 'started' && !isHost) return;
    if (!isHost && submittedByUserId !== user?.id) return;

    setPendingDeleteId(entryId);
    setConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    await handleDeleteEntry(pendingDeleteId);
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleRefreshVotes = async () => {
    if (!hostUserId || !isHost) return;
    try {
      console.log('=== MANUALLY REFRESHING VOTES ===');
      console.log('Host ID:', hostUserId);
      console.log('Competition ID:', id);
      const allVotesRes = await fetch(api(`/competitions/${hostUserId}/${id}/votes`));
      console.log('Response status:', allVotesRes.status);
      if (allVotesRes.ok) {
        const allVotesData = await allVotesRes.json();
        console.log('Refreshed votes data:', allVotesData);
        console.log('Vote counts:', allVotesData.votes);
        setAllVotes(allVotesData.votes || {});
        setSnackbarMessage('Votes refreshed!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorText = await allVotesRes.text();
        console.error('Failed to refresh votes:', allVotesRes.status, errorText);
        setSnackbarMessage('Failed to refresh votes');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to refresh votes:', error);
      setSnackbarMessage('Error refreshing votes');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Render for participants when competition is started
  const renderStartedView = () => {
      const entriesToShow = getVisibleEntries().filter(e => e.name && e.randomNumber);
      const isMultipleVotes = maxVotesPerPerson > 1;
      const votingEnabled = competitionStatus === 'started';

    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isMultipleVotes 
              ? `Vote for your favorite entries (${maxVotesPerPerson} votes total)`
              : 'Vote for your favorite entry'
            }
          </Typography>
          {!votingEnabled && (
            <Alert severity="info" sx={{ mb: 2 }}>Voting has closed.</Alert>
          )}
          {isMultipleVotes && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalVotesUsed} of {maxVotesPerPerson} votes used
            </Typography>
          )}
          {isMultipleVotes ? (
            // Multiple votes mode - can give multiple votes to same entry
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {entriesToShow.map(entry => {
              const voteCount = votesByEntry[entry.id] || 0;
              const canAddVote = totalVotesUsed < maxVotesPerPerson;
              const canEdit = competitionStatus !== 'published' && (isHost || entry.submittedByUserId === user?.id);
              const isEmpty = !entry.name;
              return (
                <Card key={entry.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {entry.randomNumber && (
                          <Chip 
                            label={`#${entry.randomNumber}`} 
                            color="primary" 
                            sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                          />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MuiIconButton 
                            size="small" 
                            onClick={() => handleRemoveVote(entry.id)}
                            disabled={voteCount === 0 || !votingEnabled}
                          >
                            <RemoveIcon />
                          </MuiIconButton>
                          <Chip 
                            label={voteCount} 
                            color={voteCount > 0 ? 'success' : 'default'}
                            sx={{ minWidth: 40 }}
                          />
                          <MuiIconButton 
                            size="small" 
                            onClick={() => handleAddVote(entry.id)}
                            disabled={!canAddVote || !votingEnabled}
                          >
                            <AddIcon />
                          </MuiIconButton>
                        </Box>
                      </Box>
                    </Box>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Add your notes about this entry..."
                      value={entryNotes[entry.id] || ''}
                      onChange={(e) => handleSaveNotes(entry.id, e.target.value)}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              );
              })}
            </Box>
          ) : (
            // Radio button mode for single vote
            <RadioGroup sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} value={Object.keys(votesByEntry)[0] || ''} onChange={(e) => handleToggleVote(e.target.value)}>
              {entriesToShow.map(entry => (
                <Card key={entry.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {entry.randomNumber && (
                          <Chip 
                            label={`#${entry.randomNumber}`} 
                            color="primary" 
                            sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                          />
                        )}
                                <FormControlLabel
                                  value={entry.id}
                                  control={<Radio />}
                                  label="Vote for this"
                                  disabled={!votingEnabled}
                                />
                        
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Add your notes about this entry..."
                      value={entryNotes[entry.id] || ''}
                      onChange={(e) => handleSaveNotes(entry.id, e.target.value)}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
          )}
        </Box>
        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Overall notes"
            value={votingNotes}
            onChange={(e) => setVotingNotes(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleSaveVote}
            disabled={!votingEnabled || !votesChanged}
            sx={{ mt: 2 }}
          >
            {hasVoted
              ? 'Update votes (saved previously)'
              : `Submit up to ${maxVotesPerPerson} votes`
            }
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Votes can be changed until voting closes
          </Typography>
        </Box>
      </Box>
    );
  };

  // Render for hosts when voting has stopped: show all entries sorted by total votes
  const renderStoppedViewHost = () => {
    const visibleEntries = getVisibleEntries();
    // Include all entries, sort by allVotes descending (default 0)
    const sorted = [...visibleEntries].sort((a, b) => {
      const votesA = allVotes[a.id] || 0;
      const votesB = allVotes[b.id] || 0;
      return votesB - votesA;
    });

    if (sorted.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6">No entries yet</Typography>
        </Box>
      );
    }

    return (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
            {sorted.map(entry => {
              const canEdit = competitionStatus !== 'published' && (isHost || entry.submittedByUserId === user?.id);
              const isEmpty = !entry.name;
              const voteCount = allVotes[entry.id] || 0;
              const isUnpublished = competitionStatus === 'started' && entry.publishedAt && lastPublishedAt && 
                                    millisGT((entry as any).publishedAt, lastPublishedAt);

              return (
                <Card key={entry.id} sx={{ 
                  flex: '1 1 320px',
                  minWidth: 240,
                  boxSizing: 'border-box',
                  opacity: isEmpty ? 0.6 : 1,
                  border: isUnpublished ? 2 : 0,
                  borderColor: isUnpublished ? 'warning.main' : 'transparent',
                }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.randomNumber && (
                          <Chip 
                            label={`#${entry.randomNumber}`} 
                            size="small" 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        <Chip 
                          label={`${voteCount} vote${voteCount !== 1 ? 's' : ''}`} 
                          size="small"
                          color={voteCount > 0 ? 'success' : 'default'}
                          sx={{ minWidth: 40, fontWeight: 'bold' }}
                        />
                      </Box>
                      <Typography variant="h6">
                        {entry.name || '(Empty entry)'}
                      </Typography>
                    </Box>
                    {canEdit && (
                      <Box>
                        <IconButton size="small" onClick={() => handleEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRequestDelete(entry.id, entry.submittedByUserId)} 
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {entry.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {entry.description}
                    </Typography>
                  )}
                  {competitionType === 'Wine' && (
                    <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box>
                        {((entry as any).imageUrl || (entry as any).imagePath) ? (
                          <EntryImage src={getEntryImageSrc(entry)} alt={entry.name || 'entry image'} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                        ) : (
                          <EntryImage src={null} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2"><strong>Winery:</strong> {((entry as any).winery && String((entry as any).winery).trim() !== '') ? sentenceCase(String((entry as any).winery)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Year:</strong> {((entry as any).year !== undefined && entry.year !== null && String(entry.year).trim() !== '') ? entry.year : '--'}</Typography>
                        <Typography variant="body2"><strong>Variety:</strong> {((entry as any).variety && String((entry as any).variety).trim() !== '') ? sentenceCase(String((entry as any).variety)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Region:</strong> {((entry as any).region && String((entry as any).region).trim() !== '') ? sentenceCase(String((entry as any).region)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Alcohol:</strong> {((entry as any).alcohol && String((entry as any).alcohol).trim() !== '') ? (entry as any).alcohol : '--'}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ 
                    mt: 1, 
                    pt: 1, 
                    borderTop: 1, 
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontWeight: 600 }}
                      >
                        Participant:
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary.main"
                        sx={{ fontWeight: 500 }}
                      >
                        {(entry as any).participantName || participantNames[entry.participantEmail || ''] || entry.participantEmail || 'Not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontWeight: 600 }}
                      >
                        Entered by:
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        {(() => {
                          const e = (entry as any).enteredByEmail;
                          if (e && String(e).includes('@')) return e;
                          if (entry.submittedByUserId && submittedUserEmails[entry.submittedByUserId]) return submittedUserEmails[entry.submittedByUserId];
                          if (entry.submittedBy && entry.submittedBy.trim() !== '') return entry.submittedBy;
                          return 'Not specified';
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                  {/* If this is a participant viewing published results, show their own saved notes for each entry */}
                  {!isHost && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">Your notes</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {entryNotes[entry.id] || 'No notes'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render for participants before competition starts
  const renderDraftViewParticipant = () => {
    if (!myEntry) {
      // If no entry exists for this participant, offer a clear action to create one.
      return (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6">You don't have an entry yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Create your entry now so it will appear to the host when they start the battle.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => handleOpenDialog(user?.email)} startIcon={<AddIcon />}>Add Entry</Button>
          </Box>
        </Box>
      );
    }

    // Render a single entry card that matches the host layout but hides host-only details
    const entry = myEntry;
    const canEdit = competitionStatus !== 'published' && (isHost || entry.submittedByUserId === user?.id);
    const isEmpty = !entry.name;
    const voteCount = allVotes[entry.id] || 0;
            const isUnpublished = competitionStatus === 'started' && entry.publishedAt && lastPublishedAt &&
                                  millisGT((entry as any).publishedAt, lastPublishedAt);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ width: '100%', textAlign: 'left' }}>
          Your Entry
        </Typography>
        <Card sx={{ width: '100%', maxWidth: 640, boxSizing: 'border-box', opacity: isEmpty ? 0.6 : 1, border: isUnpublished ? 2 : 0, borderColor: isUnpublished ? 'warning.main' : 'transparent' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                <Typography variant="h6">{entry.name || '(Empty entry)'}</Typography>
                {/* Participant should not see the random number or vote count; those are host-only */}
                {isUnpublished && (
                  <Chip 
                    label="Unpublished" 
                    size="small" 
                    color="warning" 
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
              {canEdit && (
                <Box>
                  <IconButton size="small" onClick={() => handleEdit(entry)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleRequestDelete(entry.id, entry.submittedByUserId)} 
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            {entry.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {entry.description}
              </Typography>
            )}

            {/* Layout: image/placeholder left, wine fields right. Always show fields with '--' when missing */}
            {competitionType === 'Wine' && (
              <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Box>
                  {/* Show real image to host or when published, and also show to the participant who submitted this entry */}
                    {(isHost || competitionStatus === 'published' || entry.submittedByUserId === user?.id || (user?.email && String(entry.participantEmail || '').trim().toLowerCase() === String(user.email).trim().toLowerCase())) ? (
                    ((entry as any).imageUrl || (entry as any).imagePath) ? (
                      <EntryImage src={getEntryImageSrc(entry)} alt={entry.name || 'entry image'} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                    ) : (
                      <EntryImage src={null} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                    )
                  ) : (
                    // show placeholder for other participants when not published
                    <EntryImage src={null} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                  )}
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 100 }}>
                  <Typography variant="body2"><strong>Winery:</strong> {((entry as any).winery && String((entry as any).winery).trim() !== '') ? sentenceCase(String((entry as any).winery)) : '--'}</Typography>
                  <Typography variant="body2"><strong>Year:</strong> {((entry as any).year !== undefined && entry.year !== null && String(entry.year).trim() !== '') ? entry.year : '--'}</Typography>
                  <Typography variant="body2"><strong>Variety:</strong> {((entry as any).variety && String((entry as any).variety).trim() !== '') ? sentenceCase(String((entry as any).variety)) : '--'}</Typography>
                  <Typography variant="body2"><strong>Region:</strong> {((entry as any).region && String((entry as any).region).trim() !== '') ? sentenceCase(String((entry as any).region)) : '--'}</Typography>
                  <Typography variant="body2"><strong>Alcohol:</strong> {((entry as any).alcohol && String((entry as any).alcohol).trim() !== '') ? (entry as any).alcohol : '--'}</Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ 
              mt: 1, 
              pt: 1, 
              borderTop: 1, 
              borderColor: 'divider'
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5
              }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ fontWeight: 600 }}
                >
                  Participant:
                </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary.main"
                        sx={{ fontWeight: 500 }}
                      >
                        {(entry as any).participantName || participantNames[entry.participantEmail || ''] || entry.participantEmail || 'Not specified'}
                      </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ fontWeight: 600 }}
                >
                  Entered by:
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  {(() => {
                    const e = (entry as any).enteredByEmail;
                    if (e && String(e).includes('@')) return e;
                    if (entry.submittedByUserId && submittedUserEmails[entry.submittedByUserId]) return submittedUserEmails[entry.submittedByUserId];
                    if (entry.submittedBy && entry.submittedBy.trim() !== '') return entry.submittedBy;
                    return 'Not specified';
                  })()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Waiting for host to start the battle...
        </Typography>
      </Box>
    );
  };

  // Render for host
  const renderDraftViewHost = () => {
    // Sort entries by vote count if competition is started
    const visibleEntries = getVisibleEntries();
    const sortedEntries = competitionStatus === 'started'
      ? [...visibleEntries].sort((a, b) => {
          const votesA = allVotes[a.id] || 0;
          const votesB = allVotes[b.id] || 0;
          return votesB - votesA; // Descending order
        })
      : visibleEntries;

    // Show all entries for host (including placeholders). If there are no
    // entries at all, prompt the host to add entries.
    const entriesToShow = sortedEntries;

    if (entriesToShow.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6">No entries yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Invite participants or add entries for participants to get started.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="contained" onClick={() => handleOpenDialog()} startIcon={<AddIcon />}>Add Entry</Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {entriesToShow.map(entry => {
            const canEdit = competitionStatus !== 'published' && (isHost || entry.submittedByUserId === user?.id);
            const isEmpty = !entry.name;
            const voteCount = allVotes[entry.id] || 0;
            const isUnpublished = competitionStatus === 'started' && entry.publishedAt && lastPublishedAt && 
                                  millisGT((entry as any).publishedAt, lastPublishedAt);

            return (
                <Card key={entry.id} sx={{ 
                  flex: '1 1 320px',
                  minWidth: 240,
                  boxSizing: 'border-box',
                  opacity: isEmpty ? 0.6 : 1,
                  border: isUnpublished ? 2 : 0,
                  borderColor: isUnpublished ? 'warning.main' : 'transparent',
                }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.randomNumber && (
                          <Chip 
                            label={`#${entry.randomNumber}`} 
                            size="small" 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        <Chip 
                          label={`${voteCount} vote${voteCount !== 1 ? 's' : ''}`} 
                          size="small"
                          color={voteCount > 0 ? 'success' : 'default'}
                          sx={{ minWidth: 40, fontWeight: 'bold' }}
                        />
                      </Box>
                      <Typography variant="h6">
                        {entry.name || '(Empty entry)'}
                      </Typography>
                      {isUnpublished && (
                        <Chip 
                          label="Unpublished" 
                          size="small" 
                          color="warning" 
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                    {canEdit && (
                      <Box>
                        <IconButton size="small" onClick={() => handleEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRequestDelete(entry.id, entry.submittedByUserId)} 
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {/* Vote count shown below the title and above the description */}
                  {/* vote count shown in header; remove duplicate below title for host during started state */}

                  {entry.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {entry.description}
                    </Typography>
                  )}
                  {/* Layout: image/placeholder left, wine fields right. Always show fields with '--' when missing */}
                  {competitionType === 'Wine' && (
                      <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                      <Box>
                        {((entry as any).imageUrl || (entry as any).imagePath) ? (
                          <EntryImage src={getEntryImageSrc(entry)} alt={entry.name || 'entry image'} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                        ) : (
                          <EntryImage src={null} size={100} placeholderText={isFood ? 'Add image' : undefined} />
                        )}
                      </Box>
                      <Box sx={{ flex: '1 1 200px', minWidth: 100 }}>
                        <Typography variant="body2"><strong>Winery:</strong> {((entry as any).winery && String((entry as any).winery).trim() !== '') ? sentenceCase(String((entry as any).winery)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Year:</strong> {((entry as any).year !== undefined && entry.year !== null && String(entry.year).trim() !== '') ? entry.year : '--'}</Typography>
                        <Typography variant="body2"><strong>Variety:</strong> {((entry as any).variety && String((entry as any).variety).trim() !== '') ? sentenceCase(String((entry as any).variety)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Region:</strong> {((entry as any).region && String((entry as any).region).trim() !== '') ? sentenceCase(String((entry as any).region)) : '--'}</Typography>
                        <Typography variant="body2"><strong>Alcohol:</strong> {((entry as any).alcohol && String((entry as any).alcohol).trim() !== '') ? (entry as any).alcohol : '--'}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ 
                    mt: 1, 
                    pt: 1, 
                    borderTop: 1, 
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontWeight: 600 }}
                      >
                        Participant:
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary.main"
                        sx={{ fontWeight: 500 }}
                      >
                        {(entry as any).participantName || participantNames[entry.participantEmail || ''] || entry.participantEmail || 'Not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontWeight: 600 }}
                      >
                        Entered by:
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        {(() => {
                          const e = (entry as any).enteredByEmail;
                          if (e && String(e).includes('@')) return e;
                          if (entry.submittedByUserId && submittedUserEmails[entry.submittedByUserId]) return submittedUserEmails[entry.submittedByUserId];
                          if (entry.submittedBy && entry.submittedBy.trim() !== '') return entry.submittedBy;
                          return 'Not specified';
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onMenuClick={() => setDrawerOpen(true)} title="Wine War" />

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/home')}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/competitions')}>
              <ListItemIcon>
                <EmojiEventsIcon />
              </ListItemIcon>
              <ListItemText primary="Battles" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/settings')}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 4, mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <IconButton onClick={() => history.push('/competitions')} size="large" sx={{ p: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
              <Box>
              <Typography variant="h5">{competitionName}</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Type: {competitionType} {isHost && '• Host'} • Status: {hostChecked && !loading ? competitionStatus : 'Loading...'}
              </Typography>

              {/* Host action buttons moved below the type/status line */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                {hostChecked && !loading && isHost && competitionStatus === 'draft' && (
                  <Button 
                    variant="contained" 
                    onClick={handleStartCompetition}
                    startIcon={<PlayArrowIcon />}
                    color="success"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    disabled={!hostUserId}
                  >
                    Start Battle
                  </Button>
                )}

                {hostChecked && !loading && isHost && competitionStatus === 'started' && (
                  <>
                    {hasUnpublishedEntries && (
                      <Button 
                        variant="contained" 
                        onClick={handlePublishChanges}
                        color="warning"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Publish Changes
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleStopCompetition}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Stop Voting
                    </Button>
                  </>
                )}

                {hostChecked && !loading && isHost && competitionStatus === 'stopped' && (
                  <Button variant="contained" color="primary" onClick={handlePublishResults}>
                    Publish Results
                  </Button>
                )}

                {hostChecked && !loading && isHost && (competitionStatus === 'draft' || competitionStatus === 'started') && (
                  <Button variant="outlined" onClick={() => handleOpenDialog()}>
                    Add Entry
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {loading && <Typography>Loading...</Typography>}

        {!loading && (
          <>
            {/* Only render participant/host branches after we have checked host status to avoid
                showing participant UI briefly to hosts while role is resolving. */}
            {hostChecked ? (
              <>
                {(competitionStatus === 'started' || competitionStatus === 'stopped') && !isHost && renderStartedView()}
                {competitionStatus === 'draft' && !isHost && renderDraftViewParticipant()}
                {competitionStatus === 'draft' && isHost && renderDraftViewHost()}
                {competitionStatus === 'started' && isHost && renderDraftViewHost()}
                {competitionStatus === 'stopped' && isHost && renderStoppedViewHost()}
              </>
            ) : (
              <Typography>Loading...</Typography>
            )}

            {competitionStatus === 'published' && (
              <Box>
                {!results && <Typography>Loading...</Typography>}
                {results && (
                  <Box>
                    {isHost ? (
                      <>
                        {/* For host, reuse the stopped-view cards (sorted by votes) */}
                        {renderStoppedViewHost()}

                        {/* Voter breakdown shown after the entry cards */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Voter breakdown</Typography>
                          {Object.values(results.voterDetails || {}).map((v: any) => {
                            const entryIds: string[] = v.entryIds || [];
                            const counts: { [id: string]: number } = {};
                            entryIds.forEach((eid: string) => { counts[eid] = (counts[eid] || 0) + 1; });
                            const entriesMap = results.entries || {};
                            const parts = Object.keys(counts).map(eid => {
                              const e = (entriesMap && (entriesMap[eid] || (Array.isArray(entriesMap) && entriesMap.find((x: any) => x.id === eid)))) || null;
                              const rn = e ? (`#${e.randomNumber || e.randomNumber === 0 ? e.randomNumber : e.randomNumber}`) : eid;
                              const c = counts[eid];
                              return `${rn} (${c} vote${c !== 1 ? 's' : ''})`;
                            });
                            return (
                              <Box key={v.voterId} sx={{ mb: 1 }}>
                                <Typography>
                                  <strong>{v.voterName || v.voterId}</strong>
                                  {entryIds.length ? ` — ${parts.join(', ')}` : ' — No votes'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </>
                    ) : (
                      <Box>
                        {renderStoppedViewHost()}

                        {/* Participant overall notes (their voting/summary notes) */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="subtitle2">Your notes</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {votingNotes && votingNotes.trim() ? votingNotes : 'No notes'}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Voter breakdown</Typography>
                          {Object.values(results.voterDetails || {}).map((v: any) => {
                            const entryIds: string[] = v.entryIds || [];
                            const counts: { [id: string]: number } = {};
                            entryIds.forEach((eid: string) => { counts[eid] = (counts[eid] || 0) + 1; });
                            const entriesMap = results.entries || {};
                            const parts = Object.keys(counts).map(eid => {
                              const e = (entriesMap && (entriesMap[eid] || (Array.isArray(entriesMap) && entriesMap.find((x: any) => x.id === eid)))) || null;
                              const rn = e ? (`#${e.randomNumber || e.randomNumber === 0 ? e.randomNumber : e.randomNumber}`) : eid;
                              const c = counts[eid];
                              return `${rn} (${c} vote${c !== 1 ? 's' : ''})`;
                            });
                            return (
                              <Box key={v.voterId} sx={{ mb: 1 }}>
                                <Typography>
                                  <strong>{v.voterName || v.voterId}</strong>
                                  {entryIds.length ? ` — ${parts.join(', ')}` : ' — No votes'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        <Dialog open={confirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
          <DialogTitle>Delete entry?</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this entry?</Typography>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button onClick={handleCancelDelete} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
            <Button color="error" onClick={handleConfirmDelete} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {isHost && (
              <>
                <TextField
                  label="Participant name"
                  fullWidth
                  margin="normal"
                  value={newEntrySubmittedBy}
                  onChange={e => setNewEntrySubmittedBy(e.target.value)}
                  required
                  helperText="Name of the person entering the battle (required)"
                  disabled={participantNameDisabled}
                />
                <TextField
                  label="Participant email"
                  fullWidth
                  margin="normal"
                  value={newEntryParticipantEmail}
                  onChange={e => setNewEntryParticipantEmail(e.target.value)}
                  required
                  type="email"
                  helperText="Email of the participant (required)"
                  disabled={participantEmailDisabled}
                />
              </>
            )}
            {/* Image preview and embedded capture viewfinder */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Box sx={{ width: 200, height: 200, aspectRatio: '1 / 1', borderRadius: 3, position: 'relative', bgcolor: (!tempImageUrl && !capturedImage && !cameraOpen) ? 'grey.100' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {cameraOpen && !capturedImage ? (
                    <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={{ facingMode: 'environment' }} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3 }} />
                  ) : capturedImage ? (
                    <EntryImage src={capturedImage} size={200} placeholderText={isFood ? 'Add image' : undefined} />
                  ) : (
                    (() => {
                      const previewSrc = (!imageCleared && (tempImageUrl || (editingEntry && (editingEntry as any).imageUrl)))
                        ? (resolveImageUrl(tempImageUrl) || resolveImageUrl((editingEntry as any).imageUrl))
                        : undefined;
                      return <EntryImage src={previewSrc} size={200} placeholderText={isFood ? 'Add image' : undefined} />;
                    })()
                  )}
                  {lookupInProgress && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', justifyContent: 'center' }}>
                {!cameraOpen && !capturedImage && (
                  <Button variant="outlined" onClick={() => setCameraOpen(true)} startIcon={<CameraAltIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {competitionType === 'Food' ? 'CAPTURE' : 'Capture / Lookup'}
                  </Button>
                )}
                {cameraOpen && !capturedImage && (
                  <>
                    <Button onClick={async () => {
                      const img = webcamRef.current?.getScreenshot();
                      if (!img) return;
                      // For Food competitions we do a simple capture without OCR/lookup
                        if (competitionType === 'Food') {
                        try {
                          const uploaded = await uploadTempImage(img, { skipLookup: true });
                          if (uploaded && (uploaded.imageUrl || uploaded.imagePath)) {
                            // prefer the server-returned public URL for display; resolve relative URLs
                            const publicUrl = uploaded.imageUrl ? resolveImageUrl(uploaded.imageUrl) : (uploaded.imagePath ? resolveImageUrl(api(`/uploads/${uploaded.imagePath}`)) : undefined);
                            if (publicUrl) {
                              setCapturedImage(publicUrl);
                              setTempImageUrl(publicUrl);
                            } else {
                              setCapturedImage(img);
                              setTempImageUrl(img);
                            }
                            if (uploaded.imagePath) setTempImagePath(uploaded.imagePath);
                          } else {
                            setCapturedImage(img);
                            setTempImageUrl(img);
                          }
                        } catch (e) {
                          console.warn('temp upload failed', e);
                          setCapturedImage(img);
                          setTempImageUrl(img);
                        }
                        setImageCleared(false);
                        setScanning(false);
                        setCameraOpen(false);
                        return;
                      }
                      setCapturedImage(img);
                      setTempImageUrl(img);
                      setTempImagePath(null);
                      setImageCleared(false);
                      setScanning(true);
                      try {
                        // First try barcode decode (preprocessed then original)
                        let barcode: string | null = null;
                        try {
                          const processed = await preprocessForBarcode(img);
                          const pImg = new Image();
                          pImg.src = processed;
                          await new Promise((r) => { pImg.onload = r; pImg.onerror = r; });
                          const codeReader = new BrowserMultiFormatReader();
                          try {
                            const result: any = await codeReader.decodeFromImageElement(pImg);
                            barcode = result && (typeof result.getText === 'function' ? result.getText() : (result.text || result)) || null;
                          } catch (e) {}
                        } catch (err) { console.warn('preprocess decode failed', err); }

                        if (!barcode) {
                          try {
                            const origImg = new Image();
                            origImg.src = img;
                            await new Promise((r) => { origImg.onload = r; origImg.onerror = r; });
                            const codeReader2 = new BrowserMultiFormatReader();
                            try {
                              const result2: any = await codeReader2.decodeFromImageElement(origImg);
                              barcode = result2 && (typeof result2.getText === 'function' ? result2.getText() : (result2.text || result2)) || null;
                            } catch (e) {}
                          } catch (err) { /* ignore */ }
                        }

                        if (barcode) {
                          // Barcode found — query backend and populate
                          setLookupInProgress(true);
                          try {
                            const resp = await fetch(api(`/api/lookup/barcode/${encodeURIComponent(barcode)}`));
                            if (resp.ok) {
                              const body = await resp.json();
                              if (body && body.status === 1 && body.product) {
                                const p = body.product;
                                // Apply product fields from barcode lookup (name, winery, variety, region, alcohol, year)
                                if (p) {
                                  applyLookupCandidate(p, forceOverwriteOnNextLookup);
                                  const descParts: string[] = [];
                                  if (p.brands) descParts.push(`Brand: ${p.brands}`);
                                  if (p.categories) descParts.push(`Categories: ${p.categories}`);
                                  if (p.quantity) descParts.push(`Quantity: ${p.quantity}`);
                                  if (p.countries) descParts.push(`Countries: ${p.countries}`);
                                  if (p.url) descParts.push(`Product page: ${p.url}`);
                                  setNewEntryDescription(prev => prev || descParts.join('\n'));
                                  if (p.image_url || p.image_front_url) {
                                    setCapturedImage(p.image_url || p.image_front_url);
                                    setTempImageUrl(p.image_url || p.image_front_url);
                                    setImageCleared(false);
                                  }
                                }
                              }
                            } else {
                              // no product found
                              setToastMessage('No product details found for barcode');
                              setToastOpen(true);
                            }
                          } catch (e) {
                            console.warn('Barcode lookup failed', e);
                          }
                          setCameraOpen(false);
                          setLookupInProgress(false);
                        } else {
                          // No barcode — send PREPROCESSED image to label provider proxy
                          try {
                            const processed = await preprocessForBarcode(img);
                            const processedBlob = await (await fetch(processed)).blob();
                            // also include the original capture as an auxiliary field
                            let originalBlob: Blob | null = null;
                            try { originalBlob = await (await fetch(img)).blob(); } catch (e) { originalBlob = null; }

                            const fd = new FormData();
                            fd.append('image', processedBlob, 'capture_processed.jpg');
                            if (originalBlob) fd.append('original_image', originalBlob, 'capture.jpg');

                            setLookupInProgress(true);
                            const resp = await fetch(api('/api/lookup/wine/label'), { method: 'POST', body: fd });
                            if (resp.ok) {
                              const body = await resp.json();
                              // Try to extract top product
                              const top = body && (body.products && body.products[0]);
                              if (top) {
                                applyLookupCandidate(top, forceOverwriteOnNextLookup);
                                if (body && body.ocr) setNewEntryDescription(prev => prev || body.ocr || '');
                                // If provider returned an image or details, prefer to use it
                                if (top.imageUrl || top.image_url) {
                                  setCapturedImage(top.imageUrl || top.image_url);
                                  setTempImageUrl(top.imageUrl || top.image_url);
                                  setImageCleared(false);
                                }
                              }
                              if (!top) {
                                setToastMessage('No details found for this label');
                                setToastOpen(true);
                              }
                            } else {
                              console.warn('Label lookup failed', resp.status);
                              // fallback: upload temp so user can attach later
                              try { await uploadTempImage(img); } catch (e) { console.warn('temp upload failed', e); }
                              setToastMessage('Label lookup failed');
                              setToastOpen(true);
                            }
                            setLookupInProgress(false);
                          } catch (e) {
                            console.error('Label lookup request failed', e);
                            try { await uploadTempImage(img); } catch (ee) { console.warn('temp upload failed', ee); }
                            setLookupInProgress(false);
                          }
                          setCameraOpen(false);
                        }
                      } finally { setScanning(false); }
                    }} sx={{ width: { xs: '100%', sm: 'auto' } }}>Capture</Button>
                    <Button onClick={() => setCameraOpen(false)} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                  </>
                )}
                {capturedImage && (
                    <>
                    <Button onClick={() => {
                      // Discard captured image and re-open live viewfinder for recapture
                      setCapturedImage(null);
                      setTempImageUrl(null);
                      setTempImagePath(null);
                      setImageCleared(true);
                      setCameraOpen(true);
                    }}>Recapture</Button>
                    <Button onClick={() => { setCapturedImage(null); setTempImageUrl(null); setTempImagePath(null); setImageCleared(true); }} sx={{ width: { xs: '100%', sm: 'auto' } }}>Remove</Button>
                  </>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <TextField
                label={competitionType === 'Food' ? 'Food name' : 'Wine name'}
                required
                fullWidth
                margin="normal"
                value={newEntryName}
                onChange={e => setNewEntryNameTitle(e.target.value)}
                InputProps={{
                  endAdornment: newEntryName ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setNewEntryName('')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }}
              />
            </Box>
            {competitionType === 'Wine' && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    label="Year"
                    value={newEntryYear ?? ''}
                    onChange={e => setNewEntryYear(e.target.value ? Number(e.target.value) : null)}
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    {yearOptions.map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Winery"
                  value={newEntryWinery}
                  onChange={e => setNewEntryWineryTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Variety"
                  value={newEntryVariety}
                  onChange={e => setNewEntryVarietyTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Region"
                  value={newEntryRegion}
                  onChange={e => setNewEntryRegionTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Alcohol %"
                  type="number"
                  value={newEntryAlcohol ?? ''}
                  onChange={e => setNewEntryAlcohol(e.target.value)}
                  helperText="Optional"
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MuiIconButton size="small" onClick={decrementAlcohol} edge="end">
                          <RemoveIcon />
                        </MuiIconButton>
                        <MuiIconButton size="small" onClick={incrementAlcohol} edge="end">
                          <AddIcon />
                        </MuiIconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
            {/* single preview shown above the name field; duplicated preview removed */}
            <TextField
              label="Description (optional)"
              fullWidth
              margin="normal"
              value={newEntryDescription}
              onChange={e => setNewEntryDescription(e.target.value)}
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button onClick={handleCloseDialog} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
            <Button 
              onClick={handleSaveEntry} 
              variant="contained"
              disabled={saving || !newEntryName.trim() || (isHost && (!newEntrySubmittedBy.trim() || !newEntryParticipantEmail.trim()))}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {saving ? 'Saving...' : (editingEntry ? 'Update' : 'Add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* camera dialog removed — viewfinder is embedded inline inside the entry dialog */}

        {/* Snackbar for toasts */}
        <Snackbar open={toastOpen} autoHideDuration={4000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setToastOpen(false)} severity="info" sx={{ width: '100%' }}>{toastMessage}</Alert>
        </Snackbar>

        {/* Snackbar for vote save confirmations */}
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
        </Snackbar>

        {/* Lookup suggestions dialog */}
        <Dialog open={lookupDialogOpen} onClose={() => { setLookupDialogOpen(false); setPendingAttach(null); }} fullWidth maxWidth="sm">
          <DialogTitle>Lookup Suggestions</DialogTitle>
          <DialogContent>
            {lookupLoading && <Typography>Loading suggestions…</Typography>}
            {!lookupLoading && lookupResults.length === 0 && (
              <Typography>No suggestions available.</Typography>
            )}
            {!lookupLoading && lookupResults.length > 0 && (
              <RadioGroup value={selectedLookupIndex !== null ? String(selectedLookupIndex) : undefined} onChange={(e) => setSelectedLookupIndex(parseInt(e.target.value, 10))}>
                {lookupResults.map((r, i) => (
                  <FormControlLabel key={i} value={String(i)} control={<Radio />} label={
                    <Box>
                      <Typography><strong>{r.product_name || r.product_name_en || r.name || r.title || 'Suggestion'}</strong></Typography>
                      <Typography variant="body2">{r.brands || r.brands_tags || r.brand || ''}</Typography>
                    </Box>
                  } />
                ))}
              </RadioGroup>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={async () => {
              // Cancel: attach without suggestion
              setLookupDialogOpen(false);
              if (pendingAttach) {
                try {
                  await fetch(api(`/api/uploads/attach`), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: hostUserId, competitionId: id, entryId: pendingAttach.entryId, tempPath: pendingAttach.tempPath }),
                  });
                } catch (e) { console.error('Attach on cancel failed', e); }
                setPendingAttach(null);
              }
            }}>Skip suggestion</Button>
            <Button onClick={async () => {
              // Confirm selection and attach; optionally apply suggestion to entry
              if (!pendingAttach) { setLookupDialogOpen(false); return; }
              const sel = selectedLookupIndex ?? 0;
              const suggestion = lookupResults[sel];
              // If suggestion contains a product name, pre-fill the entry name
              if (suggestion && suggestion.product_name) {
                // prefer existing value, otherwise apply title-cased suggestion
                if (!newEntryName) setNewEntryNameTitle(suggestion.product_name);
              }
              // Also populate wine detail fields from suggestion when available
              try {
                if (suggestion) {
                  if (suggestion.brands) setNewEntryWineryTitle(suggestion.brands || '');
                  if (suggestion.categories) setNewEntryVarietyTitle(suggestion.categories || '');
                  if (suggestion.countries) setNewEntryRegionTitle(suggestion.countries || '');
                  if (suggestion.nutriments && (suggestion.nutriments.alcohol || suggestion.nutriments.alcohol_100g)) setNewEntryAlcohol(String(suggestion.nutriments.alcohol || suggestion.nutriments.alcohol_100g));
                }
              } catch (e) {
                console.warn('Failed to populate wine fields from suggestion', e);
              }
              try {
                await fetch(api(`/api/uploads/attach`), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ userId: hostUserId, competitionId: id, entryId: pendingAttach.entryId, tempPath: pendingAttach.tempPath, suggestion }),
                });
              } catch (e) { console.error('Attach with suggestion failed', e); }
              setPendingAttach(null);
              setLookupDialogOpen(false);
            }} variant="contained">Confirm & Attach</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CompetitionDetailsPage;